import VERTC, {
  AudioSourceType,
  ConnectionState,
  type ConnectionStateChangeEvent,
  MediaType,
  type IRTCEngine,
  type LocalStreamStats,
  MirrorType,
  RoomProfileType,
  VideoCodecType,
  StreamIndex,
  VideoSourceType,
  VideoRenderMode,
} from "@volcengine/rtc";

import type { RtcJoinInfo } from "../api/client";
import type { StartupTrace } from '../telemetry/startup';
import { RtcSampler } from '../telemetry/rtc-sampler';
import { RemoteQualityMonitor, type RemoteStallInfo } from './quality-monitor';
import { withRtcTeardownTimeout } from './teardown-timeout';
import { createSDKI18n, type SDKI18n } from "../i18n";
import {
  DEFAULT_REALTIME_STREAM_CONTENT_HINT,
  DEFAULT_REALTIME_STREAM_MAX_KBPS,
} from "../realtime/default";
import { resolveRtcPublishFps, type ResolveRtcPublishFpsOptions } from "./media/publish-fps";
import { resolvePlaybackDrivenEncoderFps } from "./media/video-frame-rate";
import {
  type XmaxErrorCode,
  toXmaxSdkError,
  XmaxSdkError,
} from "../shared/sdk-error";
import { withAppleMobileVideoStageTimeout } from "../realtime/mobile-video-publish-compat";
import {
  applyUploadPreviewMediaLayout,
  observeUploadPreviewMediaLayout,
  PREVIEW_OUTPUT_LAYOUT_ATTR,
  PREVIEW_UPLOAD_FIT_ATTR,
  PREVIEW_UPLOAD_LAYOUT_ATTR,
} from "./render/preview-media-layout";
import {
  decodeSeiMessage,
  normalizeSei,
  RtcSeiSender,
  type RemoteSeiReceivedPayload,
  type RtcSessionSeiOptions,
  type SeiGateState,
} from "./sei";

const ENABLE_RTC_TIMING_LOGS = false;

/** Resolve RTC rendering from the public `render.fit` option, then legacy markers. */
function resolveVideoRenderMode(
  container: HTMLElement,
  fit?: "cover" | "contain",
): VideoRenderMode {
  if (fit === "contain") {
    return VideoRenderMode.RENDER_MODE_FIT;
  }
  if (fit === "cover") {
    return VideoRenderMode.RENDER_MODE_HIDDEN;
  }

  if (
    container.hasAttribute(PREVIEW_UPLOAD_LAYOUT_ATTR)
    || container.hasAttribute(PREVIEW_OUTPUT_LAYOUT_ATTR)
    || container.hasAttribute("data-preview-contain")
    || container.hasAttribute(PREVIEW_UPLOAD_FIT_ATTR)
    || container.closest(`[${PREVIEW_UPLOAD_LAYOUT_ATTR}]`)
    || container.closest(`[${PREVIEW_OUTPUT_LAYOUT_ATTR}]`)
    || container.closest("[data-preview-contain]")
    || container.closest(`[${PREVIEW_UPLOAD_FIT_ATTR}]`)
  ) {
    return VideoRenderMode.RENDER_MODE_FIT;
  }

  if (
    container.hasAttribute("data-preview-cover")
    || container.hasAttribute("data-upload-preview-cover")
    || container.closest("[data-preview-cover]")
    || container.closest("[data-upload-preview-cover]")
  ) {
    return VideoRenderMode.RENDER_MODE_HIDDEN;
  }

  const rect = container.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return VideoRenderMode.RENDER_MODE_FIT;
  }

  const isCompactPreview = rect.width <= 160;
  const isPortraitFrame = rect.height > rect.width;
  // FILL stretches non-uniformly; HIDDEN scales proportionally to fill (CSS cover).
  return isCompactPreview || isPortraitFrame
    ? VideoRenderMode.RENDER_MODE_HIDDEN
    : VideoRenderMode.RENDER_MODE_FIT;
}

export interface RtcLogEntry {
  level: "info" | "success" | "warn" | "error";
  message: string;
  data?: unknown;
}

export interface RtcStateSnapshot {
  roomId: string | null;
  userId: string | null;
  appId: string | null;
  botUserId: string | null;
  joined: boolean;
  localVideoPublished: boolean;
  remoteVideoUserId: string | null;
  users: string[];
}

interface RtcManagerOptions {
  /** @internal Passive startup diagnostics; contains no business callbacks. */
  diagnostics?: StartupTrace;
  /** SDK-managed local/remote media fit from RealtimeRenderSetting. */
  renderFit?: "cover" | "contain";
  /** Whether to subscribe to and play remote output audio. */
  playRemoteAudio?: boolean;
  onLog?: (entry: RtcLogEntry) => void;
  onStateChange?: (state: RtcStateSnapshot) => void;
  onSeiGateChange?: (state: SeiGateState) => void;
  /** Fired when Volcengine RTC reports an unrecoverable asynchronous engine error. */
  onError?: (error: RtcAsyncErrorInfo) => void;
  /** Fired when the first remote video frame is rendered for the active bot stream. */
  onRemoteVideoFirstFrame?: (info: {
    userId: string;
    width: number;
    height: number;
  }) => void;
  /** Fired for H.264 SEI received on a remote (non-local) stream. */
  onRemoteSeiReceived?: (payload: RemoteSeiReceivedPayload) => void;
  /** Fired when remote output stops presenting new frames after playback had started. */
  onRemoteOutputStalled?: (info: RemoteStallInfo) => void;
  /** Fired after the remote player is mounted (used to forward MediaStream via onRemoteStream). */
  onRemotePlayerMounted?: () => void;
  /** Fired when an RTC room message is received and parsed. */
  onRoomEvent?: (event: Record<string, unknown>) => void;
  /** Fired after a remote user joins the current RTC room. */
  onUserJoined?: (userId: string) => void;
  debug?: boolean;
  i18n?: SDKI18n;
}

export type RtcAsyncErrorInfo = {
  errorCode: string;
  forbiddenTime?: number;
  cause: unknown;
};

type VideoSize = [number, number];
type VideoContentHint = "text" | "motion" | "detail";
type VideoDegradationPreference = Extract<
  RTCDegradationPreference,
  "maintain-framerate" | "maintain-resolution"
>;
type RtcVideoEncoderPreferenceOptions = ResolveRtcPublishFpsOptions & {
  maxKbps?: number;
  contentHint?: VideoContentHint;
  audioTrack?: MediaStreamTrack;
};
export type ExternalVideoPublishDeadlines = {
  externalSourceMs: number;
  encoderMs: number;
  publishMs: number;
  timeoutMessage: string;
};
type RtcVideoDiagnosticStats = {
  width?: number;
  height?: number;
  inputFrameRate?: number;
  sentResolutionWidth?: number;
  sentResolutionHeight?: number;
  sentKBitrate?: number;
  targetKBitrate?: number;
  encodedBitrate?: number;
  encoderOutputFrameRate?: number;
  encodedFrameWidth?: number;
  encodedFrameHeight?: number;
  videoLossRate?: number;
  rtt?: number;
  statsInterval?: number;
  receivedResolutionWidth?: number;
  receivedResolutionHeight?: number;
  decodedFrameWidth?: number;
  decodedFrameHeight?: number;
  sentFrameRate?: number;
  encodeFrameRate?: number;
  receivedFrameRate?: number;
  decoderOutputFrameRate?: number;
  renderFrameRate?: number;
  rendererOutputFrameRate?: number;
  stallCount?: number;
  stallDuration?: number;
};

function readRtcVideoStat(
  stats: RtcVideoDiagnosticStats,
  keys: Array<keyof RtcVideoDiagnosticStats>,
): number {
  for (const key of keys) {
    const value = stats[key];
    if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
      return value;
    }
  }
  return 0;
}

type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    cb: (now: DOMHighResTimeStamp, metadata: { mediaTime: number }) => void,
  ) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

const REMOTE_OUTPUT_STALL_MS = 20000;
const REMOTE_OUTPUT_RECOVERY_COOLDOWN_MS = 60000;
/** First-output watchdog: cleared only after a remote frame is actually presented. */
export const REMOTE_FIRST_OUTPUT_TIMEOUT_MS = 15_000;
export const RTC_RECONNECT_TIMEOUT_MS = 60_000;
const DEFAULT_VIDEO_FRAME_RATE = 24;

export type LocalOutboundStallInfo = {
  reason: "zero-sent-frame-rate" | "track-ended" | "track-muted";
  consecutiveZeroStats: number;
  stallMs: number;
  lastSentFrameRate: number;
};

type LocalOutboundWatchState = {
  onStalled: (info: LocalOutboundStallInfo) => void;
  minZeroSamples: number;
  minStallMs: number;
  resumeGraceMs: number;
  consecutiveZeroStats: number;
  lastPositiveAtMs: number;
  graceUntilMs: number;
  triggered: boolean;
  track: MediaStreamTrack | null;
  onTrackEnded: () => void;
  onTrackMute: () => void;
  onTrackUnmute: () => void;
  onVisibilityChange: () => void;
  muteTimer: ReturnType<typeof setTimeout> | null;
  muteTriggerMs: number;
};

type LocalOutboundReadyWaiter = {
  positiveSamples: number;
  minPositiveSamples: number;
  resolve: () => void;
  reject: (error: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
};

function normalizeRtcError(
  error: unknown,
  fallbackCode: XmaxErrorCode,
  message: string,
): XmaxSdkError {
  return toXmaxSdkError(error, {
    code: fallbackCode,
    message,
  });
}

export class RtcManager {
  private engine: IRTCEngine | null = null;
  private leaveTask: Promise<void> | null = null;
  private stopPublishingTask: Promise<void> | null = null;
  private readonly users = new Set<string>();
  private currentJoinInfo: RtcJoinInfo | null = null;
  private joined = false;
  private preserveRemoteDom = false;
  private localVideoPublished = false;
  private localAudioPublished = false;
  private internalVideoCaptureStarted = false;
  private internalAudioCaptureStarted = false;
  private localVideoSourceType: VideoSourceType = VideoSourceType.VIDEO_SOURCE_TYPE_INTERNAL;
  private externalVideoTrack: MediaStreamTrack | null = null;
  private externalAudioTrack: MediaStreamTrack | null = null;
  private remoteVideoUserId: string | null = null;
  private pendingRemoteVideoUserId: string | null = null;
  private pendingRemoteVideoMediaType: MediaType = MediaType.VIDEO;
  /** Subscribed to RTC media; render may still wait on SEI match. */
  private subscribedRemoteUserId: string | null = null;
  private subscribedRemoteMediaType: MediaType = MediaType.VIDEO;
  private readonly onLog: RtcManagerOptions["onLog"];
  private readonly onStateChange: RtcManagerOptions["onStateChange"];
  private readonly onSeiGateChange: RtcManagerOptions["onSeiGateChange"];
  private onError: RtcManagerOptions["onError"];
  private onRemoteVideoFirstFrame: RtcManagerOptions["onRemoteVideoFirstFrame"];
  private readonly onRemoteSeiReceived: RtcManagerOptions["onRemoteSeiReceived"];
  private onRemoteOutputStalled: RtcManagerOptions["onRemoteOutputStalled"];
  private onRemotePlayerMounted: RtcManagerOptions["onRemotePlayerMounted"];
  private onRoomEvent: RtcManagerOptions["onRoomEvent"];
  private onUserJoined: RtcManagerOptions["onUserJoined"];
  private remoteFirstOutputTimer: ReturnType<typeof setTimeout> | null = null;
  private onRemoteFirstOutputTimeout: (() => void) | null = null;
  private localVideoContainer: HTMLElement | null = null;
  private remoteVideoContainer: HTMLElement | null = null;
  private activeGenerationId: string | null = null;
  private expectedRemoteSei: string | null = null;
  private lastRemoteSei: string | null = null;
  private lastRemoteSeiUserId: string | null = null;
  private localVideoFrameRate = DEFAULT_VIDEO_FRAME_RATE;
  private readonly seiSender: RtcSeiSender;
  private seiSupport: "unknown" | "supported" | "unsupported" = "unknown";
  private hasLoggedSeiUnsupported = false;
  private readonly debug: boolean;
  private lastDebugSeiMatched: boolean | null = null;
  private lastDebugRemoteSei: string | null = null;
  private remoteFrameWatchStop: (() => void) | null = null;
  private uploadPreviewLayoutObserverStop: (() => void) | null = null;
  private remoteOutputRecoveryCooldownUntil = 0;
  private readonly i18n: SDKI18n;
  private readonly renderFit: "cover" | "contain" | undefined;
  private readonly playRemoteAudio: boolean;
  private joinedAtMs = 0;
  private publishStartedAtMs = 0;
  private lastRoomStartEventAtMs = 0;
  private localStreamStatsCount = 0;
  private firstNonZeroSentFrameRateAtMs = 0;
  private lastObservedSentFrameRate = 0;
  private localVideoDegradationPreference: VideoDegradationPreference | null = null;
  private localOutboundWatch: LocalOutboundWatchState | null = null;
  private readonly localOutboundReadyWaiters = new Set<LocalOutboundReadyWaiter>();
  private seiGateFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private seiMatchedDelayTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSentResolution = "";
  private lastReceivedResolution = "";
  private diagnostics?: StartupTrace;
  private nativeSampler?: RtcSampler;
  private readonly nativeMetricAt: Record<string, number> = {};
  private readonly diagnosticSampleAt = { up: Number.NEGATIVE_INFINITY, down: Number.NEGATIVE_INFINITY };
  private readonly remoteQuality = new RemoteQualityMonitor();
  private qualityTimer?: ReturnType<typeof setInterval>;
  private qualityHadFrame = false;
  private qualityUserId?: string;
  private qualityWasHidden = false;
  private readonly onQualityVisibility = () => {
    this.remoteQuality.suspend(performance.now());
    this.qualityWasHidden = document.visibilityState === 'hidden';
  };
  private diagnosticConnectionChanges = 0;
  private connectionState?: ConnectionState;
  private connectionReason?: string;
  private reconnectStartedAt?: number;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private connectionRecoveredAt = 0;
  private connectionInterruptedAtEpoch?: number;
  private connectionRecoveredAtEpoch?: number;
  private tearingDown = false;
  private static readonly SEI_GATE_FALLBACK_MS = 6000;
  private static readonly SEI_MATCHED_DISPLAY_DELAY_MS = 200;

  constructor(options: RtcManagerOptions = {}) {
    this.diagnostics = options.diagnostics;
    this.diagnostics?.setContext({ rtc_sdk_version: VERTC.getSdkVersion?.() });
    this.renderFit = options.renderFit;
    this.playRemoteAudio = options.playRemoteAudio === true;
    this.onLog = options.onLog;
    this.onStateChange = options.onStateChange;
    this.onSeiGateChange = options.onSeiGateChange;
    this.onError = options.onError;
    this.onRemoteVideoFirstFrame = options.onRemoteVideoFirstFrame;
    this.onRemoteSeiReceived = options.onRemoteSeiReceived;
    this.onRemoteOutputStalled = options.onRemoteOutputStalled;
    this.onRoomEvent = options.onRoomEvent;
    this.onUserJoined = options.onUserJoined;
    this.debug = options.debug ?? false;
    this.i18n = options.i18n ?? createSDKI18n();
    this.seiSender = new RtcSeiSender({
      canSend: () => Boolean(
        this.engine
        && this.joined
        && !this.tearingDown
        && this.isSignalingConnected()
        && this.localVideoPublished
        && this.firstNonZeroSentFrameRateAtMs > 0
        && this.seiSupport === "supported"
      ),
      send: (message, repeatCount) => {
        return this.engine?.sendSEIMessage(StreamIndex.STREAM_INDEX_MAIN, message, repeatCount);
      },
      onError: (error) => {
        this.log("warn", this.i18n.t("rtc.sendSeiFailed"), error);
      },
    });
  }

  private isSignalingConnected(): boolean {
    return this.connectionState === ConnectionState.CONNECTION_STATE_CONNECTED
      || this.connectionState === ConnectionState.CONNECTION_STATE_RECONNECTED;
  }

  beginTeardown(): void {
    this.tearingDown = true;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.seiSender.stop();
  }

  /** @internal Read cached connection evidence; never poll RTC or perform I/O here. */
  getConnectionDiagnostics(): Record<string, string | number | boolean | undefined> {
    return {
      connection_state: this.connectionState === undefined ? undefined : ConnectionState[this.connectionState],
      connection_reason: this.connectionReason,
      connection_interrupted_at_ms: this.connectionInterruptedAtEpoch,
      connection_recovered_at_ms: this.connectionRecoveredAtEpoch,
      connection_recovered: this.connectionInterruptedAtEpoch === undefined ? undefined : this.connectionRecoveredAtEpoch !== undefined,
      network_online: typeof navigator === 'undefined' ? undefined : navigator.onLine,
    };
  }

  private handleConnectionStateChange(event: ConnectionStateChangeEvent): void {
    const now = performance.now();
    const previous = this.connectionState;
    this.connectionState = event.state;
    if (!this.tearingDown && this.joined && !this.isSignalingConnected() && this.reconnectStartedAt === undefined) {
      this.connectionReason = undefined;
      this.connectionInterruptedAtEpoch = Date.now();
      this.connectionRecoveredAtEpoch = undefined;
    }
    if (event.reason !== undefined) this.connectionReason = String(event.reason);
    const params = {
      state: String(event.state), state_name: ConnectionState[event.state],
      previous_state: previous === undefined ? undefined : String(previous),
      reason: event.reason === undefined ? undefined : String(event.reason),
      last_disconnect_reason: this.connectionReason,
      reconnect_elapsed_ms: this.reconnectStartedAt === undefined ? undefined : Math.round(now - this.reconnectStartedAt),
      during_teardown: this.tearingDown,
    };
    this.diagnostics?.record('sdk_rtc_connection_state_change', params);
    this.diagnostics?.updateStats({ connection_changes: ++this.diagnosticConnectionChanges,
      connection_state: params.state, connection_state_name: params.state_name,
      connection_reason: this.connectionReason });
    if (this.tearingDown) return;
    if (this.isSignalingConnected()) {
      const recovering = this.reconnectStartedAt !== undefined;
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
      this.reconnectStartedAt = undefined;
      if (recovering) {
        this.connectionRecoveredAt = now;
        this.connectionRecoveredAtEpoch = Date.now();
        this.remoteQuality.reset();
        const watch = this.localOutboundWatch;
        if (watch) { watch.consecutiveZeroStats = 0; watch.lastPositiveAtMs = now; watch.graceUntilMs = now + watch.minStallMs; }
        this.nativeSampler?.stop();
        this.nativeSampler = undefined;
        this.startNativeSampler();
        if (this.seiSender.configured) this.seiSender.start();
      }
      return;
    }
    this.seiSender.stop();
    if (!this.joined || this.reconnectStartedAt !== undefined) return;
    this.reconnectStartedAt = now;
    this.remoteQuality.reset();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      if (this.tearingDown || this.isSignalingConnected()) return;
      this.onRemoteOutputStalled?.({
        userId: this.remoteVideoUserId ?? this.qualityUserId ?? '',
        reason: 'rtc_reconnect_timeout', stallMs: Math.round(performance.now() - now),
        diagnostics: { detection_source: 'rtc_connection', connection_state: String(this.connectionState),
          connection_reason: this.connectionReason, reconnect_timeout_ms: RTC_RECONNECT_TIMEOUT_MS },
      });
    }, RTC_RECONNECT_TIMEOUT_MS);
  }

  private async teardownOperation<T>(stage: string, operation: () => T | Promise<T>): Promise<T> {
    const start = performance.now();
    this.diagnostics?.record('sdk_rtc_teardown', { operation: stage, outcome: 'started' });
    try {
      const result = await withRtcTeardownTimeout(operation, stage);
      this.diagnostics?.record('sdk_rtc_teardown', { operation: stage, outcome: 'success', duration_ms: Math.round(performance.now() - start) });
      return result;
    } catch (error) {
      this.diagnostics?.record('sdk_rtc_teardown', { operation: stage, outcome: 'error',
        duration_ms: Math.round(performance.now() - start),
        error_name: error instanceof Error ? error.name : 'UnknownError',
        error_code: String((error as { code?: unknown })?.code ?? 'WEB_RTC_ERROR'),
        connection_state: String(this.connectionState), connection_reason: this.connectionReason });
      throw error;
    }
  }

  /** @internal Allows an explicit manual start to have its own startup trace. */
  setDiagnostics(trace?: StartupTrace): void {
    this.diagnostics = trace;
  }

  private startNativeSampler(): void {
    try {
      const pc = this.engine?.peerConnection;
      if (!this.joined || this.leaveTask || !pc?.getStats || this.nativeSampler || !this.diagnostics) return;
      this.nativeSampler = new RtcSampler(pc, () => this.diagnostics?.rtcStatsEnabled === true, sample => {
        const now = performance.now();
        Object.keys(sample).forEach(key => { this.nativeMetricAt[key] = now });
        const at = this.diagnostics?.offset();
        this.diagnostics?.updateStats({
          ...sample,
          sampling_source: 'native_get_stats',
          ...(sample.up_fps !== undefined || sample.up_bitrate_kbps !== undefined ? { up_stats_at_ms: at } : {}),
          ...(sample.down_decode_fps !== undefined || sample.down_bitrate_kbps !== undefined ? { down_stats_at_ms: at } : {}),
        });
        this.diagnostics?.recordRtcStats(sample);
        if (this.qualityHadFrame && this.qualityUserId && (sample.down_bitrate_kbps !== undefined || sample.down_decode_fps !== undefined)) {
          this.remoteQuality.sample(this.qualityUserId, {
            receivedKBitrate: sample.down_bitrate_kbps,
            decoderOutputFrameRate: sample.down_decode_fps,
            receivedFrameRate: sample.down_receive_fps,
          }, performance.now());
        }
      });
    } catch { /* Native stats are optional on older RTC/browser implementations. */ }
  }

  stopRemoteQualityWatch(): void {
    clearInterval(this.qualityTimer);
    this.qualityTimer = undefined;
    this.qualityHadFrame = false;
    this.qualityUserId = undefined;
    this.remoteQuality.reset();
    if (typeof document !== 'undefined') document.removeEventListener?.('visibilitychange', this.onQualityVisibility);
  }

  private startRemoteQualityWatch(): void {
    this.stopRemoteQualityWatch();
    this.qualityWasHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    if (typeof document !== 'undefined') document.addEventListener?.('visibilitychange', this.onQualityVisibility);
    this.qualityTimer = setInterval(() => {
      const now = performance.now();
      const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      if (hidden || this.qualityWasHidden || this.reconnectStartedAt !== undefined) {
        this.remoteQuality.suspend(now);
        this.qualityWasHidden = hidden;
        return;
      }
      if (!this.qualityHadFrame) return;
      const info = this.remoteQuality.check(now);
      if (info?.reason === 'remote_stats_timeout') {
        this.diagnostics?.record('sdk_rtc_remote_state_change', {
          state: 'stats_timeout', remote_user_id: info.userId, stall_ms: info.stallMs, ...info.diagnostics,
        });
      } else if (info) this.onRemoteOutputStalled?.(info);
    }, 3000);
  }

  private recordRemoteState(userId: string, state: 'published' | 'unpublished' | 'left', mediaType?: number, rtcReason?: number): void {
    if (!this.isRemoteSeiUser(userId) && this.qualityUserId !== userId) return;
    this.diagnostics?.record('sdk_rtc_remote_state_change', { remote_user_id: userId, state, media_type: mediaType, rtc_reason_code: rtcReason });
    if (state !== 'published' && this.qualityHadFrame && this.qualityUserId === userId) {
      this.remoteQuality.missing(userId, state === 'left' ? 'remote_user_left' : 'remote_unpublished', performance.now(), rtcReason);
    }
  }

  private recordDiagnosticStats(direction: 'up' | 'down', stats: unknown, audioStats?: unknown): void {
    try { this.recordDiagnosticStatsInternal(direction, stats) } catch { /* Probe errors must not escape the RTC callback. */ }
  }

  private recordDiagnosticStatsInternal(direction: 'up' | 'down', stats: unknown): void {
    this.startNativeSampler();
    if (!this.diagnostics || this.diagnostics.done || !stats || typeof stats !== 'object') return;
    const now = performance.now();
    if (now - this.diagnosticSampleAt[direction] < 250) return;
    this.diagnosticSampleAt[direction] = now;
    const value = stats as Record<string, unknown>;
    const fields = direction === 'up'
      ? { sentFrameRate: 'up_fps', sentKBitrate: 'up_bitrate_kbps', videoLossRate: 'up_loss_rate', rtt: 'up_rtt_ms' }
      : { receivedKBitrate: 'down_bitrate_kbps', receivedFrameRate: 'down_receive_fps', decoderOutputFrameRate: 'down_decode_fps', rendererOutputFrameRate: 'down_render_fps', videoLossRate: 'down_loss_rate', rtt: 'down_rtt_ms' };
    const sample: Record<string, number | boolean> = { [`${direction}_stats_available`]: true };
    for (const [source, target] of Object.entries(fields)) {
      const metric = value[source];
      if (typeof metric === 'number' && Number.isFinite(metric)) sample[target] = metric;
    }
    sample[`${direction}_stats_at_ms`] = Math.round(performance.now() - this.diagnostics.startedAt);
    this.diagnostics.updateStats(sample);
    const metricValues: Record<string, number> = {};
    for (const target of Object.values(fields)) {
      const metric = sample[target];
      if (typeof metric === 'number') metricValues[target] = metric;
    }
    if (typeof sample.jitter_ms === 'number') metricValues.jitter_ms = sample.jitter_ms;
    let nativeFresh = false;
    Object.keys(metricValues).forEach(key => {
      if (now - (this.nativeMetricAt[key] ?? Number.NEGATIVE_INFINITY) < 2000) {
        delete metricValues[key];
        nativeFresh = true;
      }
    });
    this.diagnostics.updateStats({ sampling_source: nativeFresh ? 'native_get_stats' : 'rtc_callback' });
    this.diagnostics.recordRtcStats(metricValues, direction === 'down' ? {
      count: typeof value.stallCount === 'number' ? value.stallCount : undefined,
      durationMs: typeof value.stallDuration === 'number' ? value.stallDuration : undefined,
    } : undefined);
    if (direction === 'up' && (Number(sample.up_fps) > 0 || Number(sample.up_bitrate_kbps) > 0)) this.diagnostics.mark('outbound_started');
    if (direction === 'down' && Number(sample.down_bitrate_kbps) > 0) this.diagnostics.mark('inbound_started');
  }

  private emitSeiGateState(): void {
    const matched = this.isSeiMatched();
    this.onSeiGateChange?.({
      expectedSei: this.expectedRemoteSei,
      sei: this.lastRemoteSei,
      userId: this.lastRemoteSeiUserId,
      matched,
    });
  }

  setOnError(handler: RtcManagerOptions["onError"]): void {
    this.onError = handler;
  }

  setOnRemoteOutputStalled(
    handler: RtcManagerOptions["onRemoteOutputStalled"],
  ): void {
    this.onRemoteOutputStalled = handler;
  }

  setOnRemoteVideoFirstFrame(
    handler: RtcManagerOptions["onRemoteVideoFirstFrame"],
  ): void {
    this.onRemoteVideoFirstFrame = handler;
  }

  private emitRemoteVideoFirstFrame(info: {
    userId: string;
    width: number;
    height: number;
  }, source = 'rtc_callback'): void {
    if (this.qualityTimer !== undefined) this.remoteQuality.presented(performance.now());
    if (this.qualityTimer !== undefined && !this.qualityHadFrame) {
      this.qualityHadFrame = true;
      this.qualityUserId = info.userId;
      this.remoteQuality.sample(info.userId, {}, performance.now());
    }
    if (!this.diagnostics?.done) {
      this.diagnostics?.updateStats({ first_frame_source: source, sei_matched_at_frame: this.isSeiMatched() });
    }
    this.clearRemoteFirstOutputWatch();
    this.onRemoteVideoFirstFrame?.(info);
  }

  /**
   * Arm first-output deadline immediately after the RTC `start` event is sent.
   */
  armRemoteFirstOutputWatch(options: {
    timeoutMs?: number;
    onTimeout: () => void;
  }): void {
    this.clearRemoteFirstOutputWatch();
    const timeoutMs = options.timeoutMs ?? REMOTE_FIRST_OUTPUT_TIMEOUT_MS;
    const armedAt = performance.now();
    if (this.qualityTimer === undefined) this.startRemoteQualityWatch();
    this.onRemoteFirstOutputTimeout = options.onTimeout;
    const check = () => {
      if (this.reconnectStartedAt !== undefined) {
        this.remoteFirstOutputTimer = setTimeout(check, 3000);
        return;
      }
      const remainingMs = Math.max(armedAt, this.connectionRecoveredAt) + timeoutMs - performance.now();
      if (remainingMs > 0) {
        this.remoteFirstOutputTimer = setTimeout(check, remainingMs);
        return;
      }
      this.remoteFirstOutputTimer = null;
      const cb = this.onRemoteFirstOutputTimeout;
      this.onRemoteFirstOutputTimeout = null;
      cb?.();
    };
    this.remoteFirstOutputTimer = setTimeout(check, timeoutMs);
  }

  clearRemoteFirstOutputWatch(): void {
    if (this.remoteFirstOutputTimer !== null) {
      clearTimeout(this.remoteFirstOutputTimer);
      this.remoteFirstOutputTimer = null;
    }
    this.onRemoteFirstOutputTimeout = null;
  }

  /**
   * Last-chance synchronous verification for a missed vendor/browser frame event.
   * Reading media readiness is non-blocking and does not force layout.
   */
  confirmRemoteVideoFirstFrame(): boolean {
    const userId = this.remoteVideoUserId;
    const container = this.remoteVideoContainer;
    if (!userId || !container) {
      return false;
    }
    const video = container instanceof HTMLVideoElement
      ? container
      : container.querySelector("video");
    if (!video || video.readyState < 2 || video.videoWidth <= 0 || video.videoHeight <= 0) {
      return false;
    }
    this.emitRemoteVideoFirstFrame({
      userId,
      width: video.videoWidth,
      height: video.videoHeight,
    }, 'ready_state_fallback');
    return true;
  }

  /**
   * Passively watch RTC outbound stats after start. It never touches the source track,
   * canvas, or encoder; only sustained zero fps (or an ended track) triggers failure.
   */
  armLocalOutboundWatch(options: {
    onStalled: (info: LocalOutboundStallInfo) => void;
    minZeroSamples?: number;
    minStallMs?: number;
    startGraceMs?: number;
    resumeGraceMs?: number;
  }): void {
    this.clearLocalOutboundWatch();

    const nowMs = performance.now();
    const track = this.externalVideoTrack
      ?? this.engine?.getLocalStreamTrack(StreamIndex.STREAM_INDEX_MAIN, "video")
      ?? null;

    const state: LocalOutboundWatchState = {
      onStalled: options.onStalled,
      minZeroSamples: options.minZeroSamples ?? 3,
      minStallMs: options.minStallMs ?? 6000,
      resumeGraceMs: options.resumeGraceMs ?? 5000,
      consecutiveZeroStats: 0,
      lastPositiveAtMs: nowMs,
      graceUntilMs: nowMs + (options.startGraceMs ?? 6000),
      triggered: false,
      track,
      onTrackEnded: () => {
        this.triggerLocalOutboundStall("track-ended");
      },
      onTrackMute: () => {
        const watch = this.localOutboundWatch;
        if (!watch || watch.triggered) {
          return;
        }
        if (watch.muteTimer !== null) {
          clearTimeout(watch.muteTimer);
        }
        watch.muteTimer = setTimeout(() => {
          watch.muteTimer = null;
          if (!this.localOutboundWatch || this.localOutboundWatch !== watch || watch.triggered) {
            return;
          }
          // Ignore temporary mute while tab is backgrounded.
          if (typeof document !== "undefined" && document.visibilityState === "hidden") {
            return;
          }
          this.triggerLocalOutboundStall("track-muted");
        }, watch.muteTriggerMs);
      },
      onTrackUnmute: () => {
        const watch = this.localOutboundWatch;
        if (!watch) {
          return;
        }
        if (watch.muteTimer !== null) {
          clearTimeout(watch.muteTimer);
          watch.muteTimer = null;
        }
        watch.consecutiveZeroStats = 0;
        watch.lastPositiveAtMs = performance.now();
        watch.graceUntilMs = performance.now() + watch.resumeGraceMs;
      },
      onVisibilityChange: () => {
        const watch = this.localOutboundWatch;
        if (!watch) {
          return;
        }
        watch.consecutiveZeroStats = 0;
        watch.lastPositiveAtMs = performance.now();
        watch.graceUntilMs = document.visibilityState === "hidden"
          ? Number.POSITIVE_INFINITY
          : performance.now() + watch.resumeGraceMs;
      },
      muteTimer: null,
      muteTriggerMs: 1500,
    };

    this.localOutboundWatch = state;
    track?.addEventListener("ended", state.onTrackEnded);
    track?.addEventListener("mute", state.onTrackMute);
    track?.addEventListener("unmute", state.onTrackUnmute);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", state.onVisibilityChange);
      if (document.visibilityState === "hidden") {
        state.graceUntilMs = Number.POSITIVE_INFINITY;
      }
    }

    if (track?.readyState === "ended") {
      queueMicrotask(() => this.triggerLocalOutboundStall("track-ended"));
    }
  }

  clearLocalOutboundWatch(): void {
    const state = this.localOutboundWatch;
    if (!state) {
      return;
    }
    if (state.muteTimer !== null) {
      clearTimeout(state.muteTimer);
      state.muteTimer = null;
    }
    state.track?.removeEventListener("ended", state.onTrackEnded);
    state.track?.removeEventListener("mute", state.onTrackMute);
    state.track?.removeEventListener("unmute", state.onTrackUnmute);
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", state.onVisibilityChange);
    }
    this.localOutboundWatch = null;
  }

  setPreserveRemoteDom(preserve: boolean): void {
    this.preserveRemoteDom = preserve;
  }

  setOnRemotePlayerMounted(callback: RtcManagerOptions["onRemotePlayerMounted"]): void {
    this.onRemotePlayerMounted = callback;
  }

  setOnRoomEvent(callback: RtcManagerOptions["onRoomEvent"]): void {
    this.onRoomEvent = callback;
  }

  setOnUserJoined(callback: RtcManagerOptions["onUserJoined"]): void {
    this.onUserJoined = callback;
  }

  setLocalVideoContainer(container: HTMLElement | null): void {
    const previous = this.localVideoContainer;
    const changed = previous !== container;
    this.localVideoContainer = container;
    if (previous && changed) {
      previous.replaceChildren();
    }
    if (container && changed) {
      container.replaceChildren();
    }
    if (container && this.localVideoPublished && this.engine) {
      this.bindLocalVideoPlayer();
    }
  }

  setRemoteVideoContainer(container: HTMLElement | null): void {
    const previous = this.remoteVideoContainer;
    if (previous === container) {
      if (
        this.debug &&
        container &&
        container.childElementCount === 0 &&
        (this.remoteVideoUserId || this.pendingRemoteVideoUserId)
      ) {
        this.debugLog("setRemoteVideoContainer skipped but container empty", {
          remoteVideoUserId: this.remoteVideoUserId,
          pendingRemoteVideoUserId: this.pendingRemoteVideoUserId,
        });
      }
      return;
    }

    this.remoteVideoContainer = container;
    this.debugLog("setRemoteVideoContainer", {
      hadPrevious: !!previous,
      next: !!container,
      remoteVideoUserId: this.remoteVideoUserId,
      pendingRemoteVideoUserId: this.pendingRemoteVideoUserId,
    });
    if (previous) {
      previous.replaceChildren();
    }
    if (container && this.engine && this.joined && (this.remoteVideoUserId || this.pendingRemoteVideoUserId)) {
      this.refreshRemoteVideoBindingBestEffort();
    }
  }

  /** Tag every local encoded frame with the task id and gate display on matching remote SEI. */
  configureSessionSei(sessionId: string | null, options: RtcSessionSeiOptions = {}): void {
    const nextGeneration = sessionId?.trim() || null
    if (!nextGeneration) this.stopRemoteQualityWatch()
    else if (nextGeneration !== this.activeGenerationId) this.startRemoteQualityWatch()
    this.activeGenerationId = sessionId?.trim() || null
    const normalizedSessionId = normalizeSei(sessionId)
    const previousExpectedRemoteSei = this.expectedRemoteSei

    this.seiSender.configure(normalizedSessionId, options, this.localVideoFrameRate)
    this.clearSeiGateFallbackTimer()
    this.clearSeiMatchedDelayTimer()
    this.expectedRemoteSei = this.seiSupport === "unsupported" ? null : normalizedSessionId
    this.lastRemoteSei = null
    this.lastRemoteSeiUserId = null

    if (
      previousExpectedRemoteSei !== this.expectedRemoteSei &&
      this.remoteVideoUserId &&
      !this.preserveRemoteDom
    ) {
      this.pendingRemoteVideoUserId = this.remoteVideoUserId
      this.remoteVideoUserId = null
    }

    this.emitSeiGateState()
    if (previousExpectedRemoteSei !== this.expectedRemoteSei && !this.preserveRemoteDom) {
      this.clearContainer(this.remoteVideoContainer)
    }

    this.scheduleSeiGateFallback(normalizedSessionId)

    if (!normalizedSessionId) {
      return
    }

    this.ensureSeiSupport()
    if (this.seiSupport === "supported") {
      this.seiSender.start()
    }
  }

  private clearSeiGateFallbackTimer(): void {
    if (this.seiGateFallbackTimer !== null) {
      clearTimeout(this.seiGateFallbackTimer)
      this.seiGateFallbackTimer = null
    }
  }

  private clearSeiMatchedDelayTimer(): void {
    if (this.seiMatchedDelayTimer !== null) {
      clearTimeout(this.seiMatchedDelayTimer)
      this.seiMatchedDelayTimer = null
    }
  }

  /** Mark SEI matched if remote SEI never arrives — display gate only; RTC bind is not blocked. */
  private scheduleSeiGateFallback(expectedSei: string | null): void {
    this.clearSeiGateFallbackTimer()
    if (!expectedSei) {
      return
    }

    this.seiGateFallbackTimer = setTimeout(() => {
      this.seiGateFallbackTimer = null
      if (this.expectedRemoteSei !== expectedSei || this.isSeiMatched()) {
        return
      }
      this.log("warn", this.i18n.t("rtc.seiGateFallback"), { expectedSei })
      this.diagnostics?.updateStats({ sei_fallback: true });
      this.expectedRemoteSei = null
      this.emitSeiGateState()
      this.refreshRemoteVideoBindingBestEffort({ force: true })
    }, RtcManager.SEI_GATE_FALLBACK_MS)
  }

  get snapshot(): RtcStateSnapshot {
    return {
      roomId: this.currentJoinInfo?.roomId ?? null,
      userId: this.currentJoinInfo?.userId ?? null,
      appId: this.currentJoinInfo?.appId ?? null,
      botUserId: this.currentJoinInfo?.botName ?? null,
      joined: this.joined,
      localVideoPublished: this.localVideoPublished,
      remoteVideoUserId: this.remoteVideoUserId,
      users: Array.from(this.users),
    };
  }

  getLocalVideoSize(): { width: number; height: number } | null {
    if (!this.engine || !this.joined) {
      return null;
    }

    const track = this.engine.getLocalStreamTrack(StreamIndex.STREAM_INDEX_MAIN, "video");
    if (!track) {
      return null;
    }

    const settings = track.getSettings?.();
    const width = typeof settings?.width === "number" ? settings.width : 0;
    const height = typeof settings?.height === "number" ? settings.height : 0;

    if (!width || !height) {
      return null;
    }

    return { width, height };
  }

  getInternalVideoTrack(): MediaStreamTrack | null {
    if (!this.engine || !this.internalVideoCaptureStarted) {
      return null;
    }
    return this.engine.getLocalStreamTrack(StreamIndex.STREAM_INDEX_MAIN, "video") ?? null;
  }

  async prepareEngine(joinInfo: RtcJoinInfo): Promise<void> {
    await this.leave();
    this.tearingDown = false;
    this.connectionState = undefined;
    this.connectionReason = undefined;
    this.reconnectStartedAt = undefined;
    this.connectionRecoveredAt = 0;
    this.connectionInterruptedAtEpoch = undefined;
    this.connectionRecoveredAtEpoch = undefined;
    this.currentJoinInfo = joinInfo;
    this.users.clear();
    this.users.add(joinInfo.userId);
    this.localVideoPublished = false;
    this.localAudioPublished = false;
    this.internalVideoCaptureStarted = false;
    this.internalAudioCaptureStarted = false;
    this.localVideoDegradationPreference = null;
    this.remoteVideoUserId = null;
    this.pendingRemoteVideoUserId = null;
    this.pendingRemoteVideoMediaType = MediaType.VIDEO;
    this.lastRemoteSei = null;
    this.lastRemoteSeiUserId = null;
    this.clearVideoContainers();

    const createEngineStartedAt = performance.now();
    const engine = VERTC.createEngine(joinInfo.appId);
    this.engine = engine;
    this.bindEngineEvents(engine);
    this.logTiming("创建并绑定 RTC Engine", createEngineStartedAt, {
      appId: joinInfo.appId,
    });
  }

  async joinPreparedRoom(): Promise<void> {
    const engine = this.engine;
    const joinInfo = this.currentJoinInfo;
    if (!engine || !joinInfo) {
      throw new XmaxSdkError("SESSION_ERROR", this.i18n.t("errors.rtcNotJoined"));
    }
    if (this.joined) {
      this.log("info", this.i18n.t("rtc.alreadyInRoom"), joinInfo);
      return;
    }

    this.log("info", this.i18n.t("rtc.joiningRoom"), joinInfo);

    const joinRoomStartedAt = performance.now();
    this.diagnostics?.record('sdk_rtc_join_request', { room_id: joinInfo.roomId });
    try {
      await engine.joinRoom(
        joinInfo.token,
        joinInfo.roomId,
        {
          userId: joinInfo.userId,
        },
        {
          isAutoPublish: false,
          isAutoSubscribeAudio: false,
          isAutoSubscribeVideo: true,
          roomProfileType: RoomProfileType.communication,
        },
      );
    } catch (error) {
      this.diagnostics?.record('sdk_rtc_join_response', {
        ok: false,
        room_id: joinInfo.roomId,
        duration_ms: Math.round(performance.now() - joinRoomStartedAt),
        error_code: 'WEB_RTC_ERROR',
      });
      throw normalizeRtcError(error, "WEB_RTC_ERROR", "Failed to join RTC room");
    }
    this.diagnostics?.record('sdk_rtc_join_response', {
      ok: true,
      room_id: joinInfo.roomId,
      duration_ms: Math.round(performance.now() - joinRoomStartedAt),
    });
    this.logTiming("joinRoom", joinRoomStartedAt, { roomId: joinInfo.roomId });

    this.joined = true;
    this.startNativeSampler();
    this.joinedAtMs = performance.now();
    this.ensureSeiSupport();
    this.emitState();
    this.log("success", this.i18n.t("rtc.joinSuccess"), this.snapshot);
    this.log("info", this.i18n.t("rtc.roomUsers", { users: this.snapshot.users.join(", ") }), this.snapshot.users);
  }

  async join(joinInfo: RtcJoinInfo): Promise<void> {
    if (this.currentJoinInfo?.roomId === joinInfo.roomId && this.joined) {
      this.log("info", this.i18n.t("rtc.alreadyInRoom"), joinInfo);
      return;
    }

    await this.prepareEngine(joinInfo);
    await this.joinPreparedRoom();
  }

  async startInternalVideoCapture(options?: {
    encoderSize?: VideoSize;
    encoderFps?: number;
    encoderMaxKbps?: number;
    encoderContentHint?: VideoContentHint;
    facingMode?: 'user' | 'environment';
  }): Promise<MediaTrackSettings> {
    if (!this.engine || !this.currentJoinInfo) {
      throw new XmaxSdkError("SESSION_ERROR", this.i18n.t("errors.rtcNotJoined"));
    }

    this.localVideoFrameRate = options?.encoderFps ?? DEFAULT_VIDEO_FRAME_RATE;

    if (options?.facingMode) {
      const captureDeviceStartedAt = performance.now();
      try {
        await this.engine.setVideoCaptureDevice(options.facingMode);
      } catch (error) {
        throw normalizeRtcError(error, "CAMERA_ERROR", "Failed to select camera device");
      }
      this.logTiming("setVideoCaptureDevice", captureDeviceStartedAt, {
        facingMode: options.facingMode,
      });
    }

    if (options?.encoderSize) {
      const encoderFrameRate = options.encoderFps ?? DEFAULT_VIDEO_FRAME_RATE;
      const encoderConfigStartedAt = performance.now();
      await this.applyVideoEncoderPreference(options.encoderSize, {
        highQuality: true,
        fps: encoderFrameRate,
        maxKbps: options.encoderMaxKbps,
        contentHint: options.encoderContentHint,
      });
      this.logTiming("setVideoEncoderConfig", encoderConfigStartedAt, {
        width: options.encoderSize[0],
        height: options.encoderSize[1],
        frameRate: encoderFrameRate,
      });
    }

    if (this.internalVideoCaptureStarted) {
      return this.engine
        .getLocalStreamTrack(StreamIndex.STREAM_INDEX_MAIN, "video")
        ?.getSettings?.() ?? {};
    }

    this.bindLocalVideoPlayer();
    await this.switchToInternalVideoSource();
    const captureStartedAt = performance.now();
    let captureSettings: MediaTrackSettings | void;
    try {
      captureSettings = await this.engine.startVideoCapture();
    } catch (error) {
      throw normalizeRtcError(error, "CAMERA_ERROR", "Failed to start camera capture");
    }
    this.internalVideoCaptureStarted = true;
    this.logTiming("startVideoCapture", captureStartedAt, { captureSettings });
    return captureSettings ?? {};
  }

  async publishInternalVideo(): Promise<MediaStreamTrack> {
    if (!this.engine || !this.currentJoinInfo || !this.joined) {
      throw new XmaxSdkError("SESSION_ERROR", this.i18n.t("errors.rtcNotJoined"));
    }
    if (!this.internalVideoCaptureStarted) {
      throw new XmaxSdkError("MEDIA_PROCESSING_ERROR", this.i18n.t("errors.videoTrackMissing"));
    }

    if (this.localVideoPublished) {
      this.log("info", this.i18n.t("rtc.localVideoAlreadyPublished"));
      const existingTrack = this.engine.getLocalStreamTrack(StreamIndex.STREAM_INDEX_MAIN, "video");
      if (!existingTrack) {
        throw new XmaxSdkError("MEDIA_PROCESSING_ERROR", this.i18n.t("errors.videoTrackMissing"));
      }
      return existingTrack;
    }

    const publishWithAudio = this.internalAudioCaptureStarted;
    const mediaType = publishWithAudio
      ? MediaType.AUDIO_AND_VIDEO
      : MediaType.VIDEO;
    const publishStreamStartedAt = performance.now();
    try {
      await this.engine.publishStream(mediaType);
    } catch (error) {
      if (!publishWithAudio) {
        throw normalizeRtcError(error, "WEB_RTC_ERROR", "Failed to publish RTC stream");
      }
      // Some mobile browsers can start mic capture but fail A/V publish.
      // Retry with video-only so camera flow continues.
      this.log("warn", "[RTC Diagnose] internal A/V publish failed; fallback to video-only", error);
      const maybeStopAudioCapture = (this.engine as unknown as { stopAudioCapture?: () => Promise<void> | void })
        .stopAudioCapture;
      if (typeof maybeStopAudioCapture === "function") {
        try {
          await maybeStopAudioCapture.call(this.engine);
        } catch {
        }
      }
      this.internalAudioCaptureStarted = false;
      await this.switchToInternalAudioSource();
      try {
        await this.engine.publishStream(MediaType.VIDEO);
      } catch (fallbackError) {
        throw normalizeRtcError(fallbackError, "WEB_RTC_ERROR", "Failed to publish RTC video stream");
      }
    }
    this.logTiming("publishStream", publishStreamStartedAt);
    const publishStartedAtMs = performance.now();

    const track = this.engine.getLocalStreamTrack(StreamIndex.STREAM_INDEX_MAIN, "video");
    if (!track) {
      throw new XmaxSdkError("MEDIA_PROCESSING_ERROR", this.i18n.t("errors.videoTrackMissing"));
    }
    await this.applyLocalVideoDegradationPreference(track);
    this.localVideoPublished = true;
    this.localAudioPublished = this.internalAudioCaptureStarted;
    this.publishStartedAtMs = publishStartedAtMs;
    this.localStreamStatsCount = 0;
    this.firstNonZeroSentFrameRateAtMs = 0;
    this.lastObservedSentFrameRate = 0;
    this.ensureSeiSupport();
    this.emitState();
    this.diagnostics?.record('sdk_rtc_publish_local_track', {
      track_kind: 'video',
      publish_duration_ms: Math.round(performance.now() - publishStreamStartedAt),
    });
    this.log("success", this.i18n.t("rtc.localVideoPublished"));
    return track;
  }

  async startInternalAudioCapture(): Promise<void> {
    if (!this.engine || !this.currentJoinInfo) {
      throw new XmaxSdkError("SESSION_ERROR", this.i18n.t("errors.rtcNotJoined"));
    }
    if (this.internalAudioCaptureStarted) {
      return;
    }
    const maybeStartAudioCapture = (this.engine as unknown as { startAudioCapture?: () => Promise<void> | void })
      .startAudioCapture;
    if (typeof maybeStartAudioCapture !== "function") {
      this.log("warn", "[Xmax][RTC] startAudioCapture API is unavailable");
      return;
    }
    try {
      await maybeStartAudioCapture.call(this.engine);
      this.internalAudioCaptureStarted = true;
    } catch (error) {
      // Browser/WebView mic capture mismatch should not block camera video flow.
      this.internalAudioCaptureStarted = false;
      this.log("warn", "[RTC Diagnose] startAudioCapture failed; fallback to video-only", error);
    }
  }

  async startExternalVideoPublishing(
    track: MediaStreamTrack,
    encoderSize: VideoSize,
    publishOptions: RtcVideoEncoderPreferenceOptions,
    deadlines?: ExternalVideoPublishDeadlines,
  ): Promise<void> {
    if (!this.engine || !this.currentJoinInfo || !this.joined) {
      throw new XmaxSdkError("SESSION_ERROR", this.i18n.t("errors.rtcNotJoined"));
    }

    if (this.localVideoPublished) {
      this.log("info", this.i18n.t("rtc.localVideoAlreadyPublished"));
      return;
    }

    const publishStartedAtMs = performance.now();
    const requestedAudioTrack = publishOptions.audioTrack;
    this.log("info", "[Xmax][RTC] external publish tracks", {
      hasVideoTrack: true,
      hasAudioTrack: Boolean(requestedAudioTrack),
      audioReadyState: requestedAudioTrack?.readyState ?? null,
    });

    const encoderOptions = publishOptions.highQuality !== false
      ? {
          ...publishOptions,
          highQuality: true,
          fps: publishOptions.fps ?? resolvePlaybackDrivenEncoderFps(track),
        }
      : publishOptions;
    this.localVideoFrameRate = encoderOptions.fps ?? DEFAULT_VIDEO_FRAME_RATE;

    this.bindLocalVideoPlayer();
    await this.runExternalPublishStage(
      () => this.switchToExternalVideoSource(track),
      deadlines,
      deadlines?.externalSourceMs,
      "external_video_source",
    );
    let audioTrack: MediaStreamTrack | undefined = requestedAudioTrack;
    if (audioTrack) {
      try {
        await this.switchToExternalAudioSource(audioTrack);
      } catch (error) {
        // Some mobile browsers/WebViews reject external audio source wiring for media files.
        // Fallback to video-only publish so upload inputs remain usable.
        audioTrack = undefined;
        this.externalAudioTrack = null;
        await this.switchToInternalAudioSource();
      }
    }
    await this.runExternalPublishStage(
      () => this.applyVideoEncoderPreference(
        encoderSize,
        encoderOptions,
        { throwOnFailure: Boolean(deadlines) },
      ),
      deadlines,
      deadlines?.encoderMs,
      "video_encoder_config",
    );
    try {
      await this.runExternalPublishStage(
        () => this.engine!.publishStream(
          audioTrack ? MediaType.AUDIO_AND_VIDEO : MediaType.VIDEO,
        ),
        deadlines,
        deadlines?.publishMs,
        "publish_stream",
      );
    } catch (error) {
      if (!audioTrack) {
        throw error;
      }
      this.externalAudioTrack = null;
      audioTrack = undefined;
      await this.switchToInternalAudioSource();
      try {
        await this.engine.publishStream(MediaType.VIDEO);
      } catch (fallbackError) {
        throw normalizeRtcError(
          fallbackError,
          "WEB_RTC_ERROR",
          "Failed to publish external RTC video stream",
        );
      }
    }
    this.localVideoPublished = true;
    this.localAudioPublished = Boolean(audioTrack);
    this.publishStartedAtMs = publishStartedAtMs;
    this.localStreamStatsCount = 0;
    this.firstNonZeroSentFrameRateAtMs = 0;
    this.lastObservedSentFrameRate = 0;
    this.ensureSeiSupport();
    this.emitState();
    this.diagnostics?.record('sdk_rtc_publish_local_track', {
      track_kind: 'video',
      publish_duration_ms: Math.round(performance.now() - publishStartedAtMs),
    });
    this.log("success", this.i18n.t("rtc.externalVideoPublished"));
  }

  private async runExternalPublishStage<T>(
    operation: () => Promise<T>,
    deadlines: ExternalVideoPublishDeadlines | undefined,
    timeoutMs: number | undefined,
    stage: string,
  ): Promise<T> {
    try {
      if (!deadlines || !timeoutMs) {
        return await operation();
      }
      return await withAppleMobileVideoStageTimeout(operation, {
        stage,
        timeoutMs,
        message: deadlines.timeoutMessage,
      });
    } catch (error) {
      throw normalizeRtcError(error, "WEB_RTC_ERROR", "Failed to publish external RTC stream");
    }
  }

  async stopVideoPublishing(options?: {
    preserveExternalTrack?: boolean
    preserveRemote?: boolean
  }): Promise<void> {
    if (this.stopPublishingTask) {
      return this.stopPublishingTask;
    }
    const task = this.stopVideoPublishingInternal(options);
    this.stopPublishingTask = task;
    try {
      await task;
    } finally {
      if (this.stopPublishingTask === task) this.stopPublishingTask = null;
    }
  }

  private async stopVideoPublishingInternal(options?: {
    preserveExternalTrack?: boolean
    preserveRemote?: boolean
  }): Promise<void> {
    this.clearLocalOutboundWatch();
    this.rejectLocalOutboundReadyWaiters(
      "Local video publishing stopped",
    );
    const engine = this.engine;
    if (!engine) {
      return;
    }

    this.seiSender.stop();

    // Hardware release must not wait for the remote unpublish acknowledgement.
    for (const kind of ['video', 'audio'] as const) {
      const internal = kind === 'video' ? this.internalVideoCaptureStarted : this.internalAudioCaptureStarted;
      if (internal) {
        try { engine.getLocalStreamTrack?.(StreamIndex.STREAM_INDEX_MAIN, kind)?.stop(); } catch {}
      }
    }

    if (this.externalVideoTrack) {
      if (!options?.preserveExternalTrack) {
        try {
          this.externalVideoTrack.stop();
        } catch {
        }
      }
      this.externalVideoTrack = null;
    }
    if (this.externalAudioTrack) {
      if (!options?.preserveExternalTrack) {
        try {
          this.externalAudioTrack.stop();
        } catch {
        }
      }
      this.externalAudioTrack = null;
    }

    try {
      const operations: Array<Promise<unknown>> = [];
      if (this.localVideoPublished || this.localAudioPublished) {
        const mediaType = this.localVideoPublished && this.localAudioPublished
          ? MediaType.AUDIO_AND_VIDEO
          : (this.localAudioPublished ? MediaType.AUDIO : MediaType.VIDEO);
        operations.push(this.teardownOperation('unpublish', () => engine.unpublishStream(mediaType)));
      }
      if (this.internalVideoCaptureStarted) {
        operations.push(this.teardownOperation('stop_video_capture', () => engine.stopVideoCapture()));
      }
      if (this.internalAudioCaptureStarted) {
        operations.push(this.teardownOperation('stop_audio_capture', () => engine.stopAudioCapture()));
      }
      await Promise.all(operations);
      if (this.engine !== engine) return;
      await withRtcTeardownTimeout(async () => {
        await this.switchToInternalVideoSource(false);
        if (this.engine !== engine) return;
        await this.switchToInternalAudioSource();
      }, 'reset_media_source');
      if (this.engine !== engine) return;
    } catch (error) {
      if (this.engine === engine) this.forceDestroy(options);
      throw error;
    }
    this.clearUploadPreviewLayoutObserver();
    this.clearContainer(this.localVideoContainer);
    if (!options?.preserveRemote) {
      this.clearRemoteVideo();
    }
    this.localVideoPublished = false;
    this.localAudioPublished = false;
    this.internalVideoCaptureStarted = false;
    this.internalAudioCaptureStarted = false;
    this.localVideoDegradationPreference = null;
    this.publishStartedAtMs = 0;
    this.lastSentResolution = "";
    this.emitState();
    this.log("warn", this.i18n.t("rtc.localVideoStopped"));
  }

  async leave(options?: { preserveExternalTrack?: boolean }): Promise<void> {
    this.tearingDown = true;
    clearTimeout(this.reconnectTimer);
    this.nativeSampler?.stop();
    this.nativeSampler = undefined;
    this.stopRemoteQualityWatch();
    if (this.leaveTask) {
      await this.leaveTask;
      return;
    }

    const engine = this.engine;
    if (!engine) {
      return;
    }

    this.leaveTask = (async () => {
      try {
        if (
          this.localVideoPublished
          || this.localAudioPublished
          || this.internalVideoCaptureStarted
          || this.internalAudioCaptureStarted
          || this.externalVideoTrack
          || this.externalAudioTrack
        ) {
          await this.stopVideoPublishing(options);
        }
        if (this.joined) {
          await this.teardownOperation('leave_room', () => engine.leaveRoom());
        }
      } catch (error) {
        this.log("warn", this.i18n.t("rtc.leaveRoomError"), error);
        throw error;
      } finally {
        try {
          engine.destroy();
          this.diagnostics?.record('sdk_rtc_teardown', { operation: 'destroy_engine', outcome: 'success' });
        } catch {
          this.diagnostics?.record('sdk_rtc_teardown', { operation: 'destroy_engine', outcome: 'error' });
        }
        if (this.engine === engine) {
          this.engine = null;
        }
        this.currentJoinInfo = null;
        this.users.clear();
        this.joined = false;
        this.localVideoPublished = false;
        this.localAudioPublished = false;
        this.internalVideoCaptureStarted = false;
        this.internalAudioCaptureStarted = false;
        this.localVideoSourceType = VideoSourceType.VIDEO_SOURCE_TYPE_INTERNAL;
        this.externalVideoTrack = null;
        this.externalAudioTrack = null;
        this.remoteVideoUserId = null;
        this.pendingRemoteVideoUserId = null;
        this.pendingRemoteVideoMediaType = MediaType.VIDEO;
        this.subscribedRemoteUserId = null;
        this.subscribedRemoteMediaType = MediaType.VIDEO;
        this.activeGenerationId = null;
        this.seiSender.stop();
        this.clearSeiGateFallbackTimer();
        this.clearSeiMatchedDelayTimer();
        this.clearRemoteFirstOutputWatch();
        this.clearLocalOutboundWatch();
        this.stopRemoteFrameWatch();
        this.joinedAtMs = 0;
        this.publishStartedAtMs = 0;
        this.lastRoomStartEventAtMs = 0;
        this.localStreamStatsCount = 0;
        this.firstNonZeroSentFrameRateAtMs = 0;
        this.lastSentResolution = "";
        this.lastReceivedResolution = "";
        this.clearVideoContainers();
        this.emitState();
        this.log("info", this.i18n.t("rtc.engineDestroyed"));
      }
    })();

    try {
      await this.leaveTask;
    } finally {
      this.leaveTask = null;
    }
  }

  /** Subscribe to the remote output stream before start. */
  async prepareRemoteVideoForGeneration(): Promise<void> {
    const botUserId = this.currentJoinInfo?.botName?.trim();
    if (!botUserId || !this.engine || !this.joined) {
      return;
    }

    this.pendingRemoteVideoUserId = botUserId;
    this.pendingRemoteVideoMediaType = MediaType.VIDEO;
    await this.ensureRemoteStreamSubscribed(botUserId, MediaType.VIDEO);
  }

  async sendRoomEvent(event: unknown): Promise<void> {
    this.ensureReadyForMessaging();
    const eventName = typeof event === "object" && event !== null && "event" in event
      ? String((event as { event?: unknown }).event ?? "unknown")
      : "unknown";

    if (eventName === "start") {
      this.lastRoomStartEventAtMs = performance.now();
    }
    try {
      const payload = JSON.stringify(event);
      if (eventName === "start") {
        const params = (event as { params?: Record<string, unknown> }).params;
        const fields = params
          ? Object.keys(params).filter(key => ['prompt', 'ref_image_path', 'static_generate', 'static_image_path'].includes(key)).join(',')
          : undefined;
        this.diagnostics?.generationSent({
          generation_id: (event as { uid?: string }).uid ?? null,
          static_generate: params?.static_generate === true,
          context_fields: fields,
        });
      }
      const engine = this.engine!;
      if (eventName === 'stop') {
        await this.teardownOperation('stop_generation', () => engine.sendRoomMessage(payload));
      } else {
        await engine.sendRoomMessage(payload);
      }
    } catch (error) {
      throw normalizeRtcError(error, "WEB_RTC_ERROR", "Failed to send RTC room message");
    }
    this.log("success", this.i18n.t("rtc.roomEventSent"), event);
  }

  /** Send a control event directly to one user in the current RTC room. */
  async sendUserEvent(userId: string, event: unknown): Promise<void> {
    this.ensureReadyForMessaging();
    const targetUserId = userId.trim();
    if (!targetUserId) {
      return;
    }
    try {
      const payload = JSON.stringify(event);
      await this.engine!.sendUserMessage(targetUserId, payload);
    } catch (error) {
      throw normalizeRtcError(error, "WEB_RTC_ERROR", "Failed to send RTC user message");
    }
    this.log("success", this.i18n.t("rtc.roomEventSent"), {
      targetUserId,
      event,
    });
  }

  /** Best-effort room control message. Returns false instead of throwing or blocking callers. */
  async trySendRoomEvent(event: unknown): Promise<boolean> {
    const engine = this.engine;
    if (!engine || !this.joined) {
      return false;
    }
    try {
      const payload = JSON.stringify(event);
      const sendTask = engine.sendRoomMessage(payload);
      if (!sendTask) {
        return false;
      }
      await sendTask;
      this.log("success", this.i18n.t("rtc.roomEventSent"), event);
      return true;
    } catch (error) {
      this.log("warn", this.i18n.t("rtc.roomEventSent"), { event, error });
      return false;
    }
  }

  /** Best-effort direct control message. Returns false instead of throwing or blocking callers. */
  async trySendUserEvent(userId: string, event: unknown): Promise<boolean> {
    const engine = this.engine;
    const targetUserId = userId.trim();
    if (!engine || !this.joined || !targetUserId) {
      return false;
    }
    try {
      const payload = JSON.stringify(event);
      const sendTask = engine.sendUserMessage(targetUserId, payload);
      if (!sendTask) {
        return false;
      }
      await sendTask;
      this.log("success", this.i18n.t("rtc.roomEventSent"), {
        targetUserId,
        event,
      });
      return true;
    } catch (error) {
      this.log("warn", this.i18n.t("rtc.roomEventSent"), {
        targetUserId,
        event,
        error,
      });
      return false;
    }
  }

  clearRemoteVideo(options?: { preserveDom?: boolean }): void {
    this.stopRemoteFrameWatch();
    if (!options?.preserveDom && !this.preserveRemoteDom) {
      this.clearContainer(this.remoteVideoContainer);
    }
    if (this.remoteVideoUserId) {
      this.pendingRemoteVideoUserId = this.remoteVideoUserId;
      this.remoteVideoUserId = null;
      this.emitState();
    }
  }

  async refreshRemoteVideoBinding(options?: { force?: boolean }): Promise<void> {
    const targetUserId = this.remoteVideoUserId ?? this.pendingRemoteVideoUserId;
    if (!this.engine || !targetUserId || !this.remoteVideoContainer) {
      return;
    }

    await this.ensureRemoteStreamSubscribed(targetUserId, this.pendingRemoteVideoMediaType);

    if (!this.canRenderRemoteVideo(targetUserId)) {
      this.debugLog("refreshRemoteVideoBinding blocked by SEI gate", {
        targetUserId,
        expectedRemoteSei: this.expectedRemoteSei,
        lastRemoteSei: this.lastRemoteSei,
      });
      return;
    }

    if (
      !options?.force &&
      this.remoteVideoUserId === targetUserId &&
      this.remoteVideoContainer &&
      this.remoteVideoContainer.childElementCount > 0
    ) {
      this.debugLog("refreshRemoteVideoBinding skipped (already mounted)", { targetUserId });
      return;
    }

    try {
      await this.mountRemoteVideoPlayer(
        targetUserId,
        this.remoteVideoContainer,
        this.pendingRemoteVideoMediaType,
        options?.force,
      );
      this.remoteVideoUserId = targetUserId;
      this.pendingRemoteVideoUserId = null;
      this.emitState();
      this.startRemoteFrameWatch(targetUserId);
      this.emitRemotePlayerMounted();
      this.log("info", this.i18n.t("rtc.remotePlayerRefreshed", { userId: targetUserId }));
    } catch (error) {
      this.log("warn", this.i18n.t("rtc.remotePlayerRefreshFailed"), error);
      throw normalizeRtcError(error, "WEB_RTC_ERROR", "Failed to refresh RTC remote video binding");
    }
  }

  private refreshRemoteVideoBindingBestEffort(options?: { force?: boolean }): void {
    void this.refreshRemoteVideoBinding(options).catch((error) => {
      this.log("warn", this.i18n.t("rtc.remotePlayerRefreshFailed"), error);
    });
  }

  private attachRemoteVideoBestEffort(userId: string, mediaType: MediaType): void {
    void this.attachRemoteVideo(userId, mediaType).catch((error) => {
      this.log("warn", this.i18n.t("rtc.remotePlayerRefreshFailed"), error);
    });
  }

  private bindEngineEvents(engine: IRTCEngine): void {
    const roomMessageHandler = (payload: unknown) => {
      const event = this.parseRoomEventPayload(payload);
      this.debugLog("room message received", {
        raw: payload,
        parsed: event,
      });
      if (event) {
        this.recordGenerationAckFromRoomEvent(event);
        this.onRoomEvent?.(event);
      }
    };
    const roomMessageEventNames = new Set<string>([
      "onRoomMessageReceived",
      "onUserMessageReceived",
    ]);
    const sdkRoomMessageEventName = (VERTC.events as Record<string, string>).onRoomMessageReceived;
    if (sdkRoomMessageEventName) {
      roomMessageEventNames.add(sdkRoomMessageEventName);
    }
    roomMessageEventNames.forEach((eventName) => {
      engine.on(eventName as any, roomMessageHandler);
    });

    engine.on(VERTC.events.onUserJoined, (event) => {
      const userId = event.userInfo.userId;
      this.users.add(userId);
      this.emitState();
      try {
        this.onUserJoined?.(userId);
      } catch {
        // Consumer callbacks must not interrupt RTC event processing.
      }
      this.log("success", this.i18n.t("rtc.userJoined", { userId }), event);
      this.log("info", this.i18n.t("rtc.roomUsers", { users: this.snapshot.users.join(", ") }), this.snapshot.users);
    });

    engine.on(VERTC.events.onUserLeave, (event) => {
      this.recordRemoteState(event.userInfo.userId, 'left', undefined, event.reason);
      this.users.delete(event.userInfo.userId);
      if (this.remoteVideoUserId === event.userInfo.userId) {
        this.remoteVideoUserId = null;
        this.clearContainer(this.remoteVideoContainer);
      }
      if (this.pendingRemoteVideoUserId === event.userInfo.userId) {
        this.pendingRemoteVideoUserId = null;
      }
      this.emitState();
      this.log("warn", this.i18n.t("rtc.userLeft", { userId: event.userInfo.userId }), event);
      this.log("info", this.i18n.t("rtc.roomUsers", { users: this.snapshot.users.join(", ") }), this.snapshot.users);
    });

    engine.on(VERTC.events.onConnectionStateChanged, (event) => {
      if (this.engine !== engine) return;
      this.handleConnectionStateChange(event);
      this.log("info", this.i18n.t("rtc.connectionStateChanged"), event);
    });

    engine.on(VERTC.events.onNetworkQuality, (up, down) => {
      this.diagnostics?.updateStats({ up_network_quality: up, down_network_quality: down });
    });

    engine.on(VERTC.events.onTokenWillExpire, () => {
      this.diagnostics?.record('sdk_rtc_token_state_change', { scope: 'room', state: 'will_expire' });
    });
    engine.on(VERTC.events.onTokenPublishPrivilegeWillExpire, () => {
      this.diagnostics?.record('sdk_rtc_token_state_change', { scope: 'publish', state: 'will_expire' });
    });
    engine.on(VERTC.events.onTokenSubscribePrivilegeWillExpire, () => {
      this.diagnostics?.record('sdk_rtc_token_state_change', { scope: 'subscribe', state: 'will_expire' });
    });
    engine.on(VERTC.events.onTokenPublishPrivilegeDidExpired, event => {
      this.diagnostics?.record('sdk_rtc_token_state_change', { scope: 'publish', state: 'expired', rtc_error_code: event.errorCode });
    });
    engine.on(VERTC.events.onTokenSubscribePrivilegeDidExpired, event => {
      this.diagnostics?.record('sdk_rtc_token_state_change', { scope: 'subscribe', state: 'expired', rtc_error_code: event.errorCode });
    });

    engine.on(VERTC.events.onUserPublishStream, (event) => {
      if (this.supportsVideo(event.mediaType)) this.recordRemoteState(event.userId, 'published', event.mediaType);
      if (this.isRemoteSeiUser(event.userId)) this.diagnostics?.mark('remote_published');
      this.log("success", this.i18n.t("rtc.userPublished", { userId: event.userId }), event);
      if (this.supportsVideo(event.mediaType)) {
        this.attachRemoteVideoBestEffort(event.userId, event.mediaType);
      }
    });

    engine.on(VERTC.events.onUserUnpublishStream, (event) => {
      if (this.supportsVideo(event.mediaType)) this.recordRemoteState(event.userId, 'unpublished', event.mediaType, event.reason);
      this.log("warn", this.i18n.t("rtc.userUnpublished", { userId: event.userId }), event);
      this.debugLog("onUserUnpublishStream", {
        userId: event.userId,
        mediaType: event.mediaType,
        remoteVideoUserId: this.remoteVideoUserId,
        expectedRemoteSei: this.expectedRemoteSei,
        lastRemoteSei: this.lastRemoteSei,
      });
      if (this.remoteVideoUserId === event.userId && this.supportsVideo(event.mediaType)) {
        this.pendingRemoteVideoUserId = event.userId;
        this.pendingRemoteVideoMediaType = event.mediaType;
        this.remoteVideoUserId = null;
        this.stopRemoteFrameWatch();
        if (!this.preserveRemoteDom) {
          this.clearContainer(this.remoteVideoContainer);
        }
        this.emitState();
        return;
      }
    });

    engine.on(VERTC.events.onSEIMessageReceived, (event) => {
      const userId = event.remoteStreamKey.userId;
      const sei = normalizeSei(decodeSeiMessage(event.sei));

      if (sei && this.isRemoteSeiUser(userId)) {
        this.onRemoteSeiReceived?.({ sei, userId });
      }

      this.lastRemoteSei = sei;
      this.lastRemoteSeiUserId = userId;

      const matched = this.isSeiMatched();
      const seiStateChanged =
        this.lastDebugSeiMatched !== matched ||
        this.lastDebugRemoteSei !== sei;

      if (this.debug && seiStateChanged) {
        this.debugLog("onSEIMessageReceived", {
          userId,
          sei,
          matched,
          expectedRemoteSei: this.expectedRemoteSei,
        });
        this.lastDebugSeiMatched = matched;
        this.lastDebugRemoteSei = sei;
      }

      if (!matched) {
        this.onSeiGateChange?.({
          expectedSei: this.expectedRemoteSei,
          sei,
          userId,
          matched: false,
        });
        return;
      }

      // If already matched and waiting for delay, do nothing
      if (this.seiMatchedDelayTimer !== null) {
        return;
      }

      this.clearSeiGateFallbackTimer();
      this.seiMatchedDelayTimer = setTimeout(() => {
        this.seiMatchedDelayTimer = null;

        // Double check if it's still matched (in case expectedRemoteSei changed during delay)
        if (!this.isSeiMatched()) {
          return;
        }

        this.onSeiGateChange?.({
          expectedSei: this.expectedRemoteSei,
          sei,
          userId,
          matched: true,
        });

        if (this.remoteVideoUserId === userId) {
          return;
        }

        this.attachRemoteVideoBestEffort(userId, this.pendingRemoteVideoMediaType);
      }, RtcManager.SEI_MATCHED_DISPLAY_DELAY_MS);
    });

    engine.on(VERTC.events.onError, (event) => {
      this.diagnostics?.updateStats({ rtc_error_code: String(event.errorCode) });
      const error: RtcAsyncErrorInfo = {
        errorCode: String(event.errorCode),
        forbiddenTime: event.forbiddenTime,
        cause: event,
      };
      this.log("error", this.i18n.t("rtc.error", { code: error.errorCode }), event);
      try {
        this.onError?.(error);
      } catch {
        // Consumer callbacks must not break the RTC event loop.
      }
    });

    engine.on(VERTC.events.onPublishResult, (event) => {
      if (!event.isScreen) {
        this.diagnostics?.updateStats({ publish_result_code: event.errorCode === undefined ? undefined : String(event.errorCode) });
        this.diagnostics?.mark('publish_result');
      }
      this.debugLog("onPublishResult", {
        ...event,
        elapsedSincePublishMs: this.publishStartedAtMs > 0
          ? Number((performance.now() - this.publishStartedAtMs).toFixed(1))
          : null,
      });
    });

    engine.on(VERTC.events.onRemoteVideoFirstFrame, (event) => {
      if (event.isScreen || !this.isRemoteSeiUser(event.userId)) {
        return;
      }
      this.emitRemoteVideoFirstFrame({
        userId: event.userId,
        width: event.width,
        height: event.height,
      });
    });

    engine.on(VERTC.events.onLocalStreamStats, (stats) => {
      if (!stats.isScreen) this.recordDiagnosticStats('up', stats.videoStats);
      this.logLocalStreamStats(stats);
      if (stats.videoStats) {
        const videoStats = stats.videoStats as RtcVideoDiagnosticStats;
        // LocalStreamStats may omit sentResolutionWidth/Height.
        // Use encodedFrameWidth/encodedFrameHeight as fallback.
        const width = readRtcVideoStat(videoStats, ["sentResolutionWidth", "encodedFrameWidth", "width"]);
        const height = readRtcVideoStat(videoStats, ["sentResolutionHeight", "encodedFrameHeight", "height"]);
        const sentFrameRate = readRtcVideoStat(videoStats, ["sentFrameRate", "encodeFrameRate"]);

        this.logVideoFrameDiagnostics("send", width, height, sentFrameRate, undefined, {
          configuredFrameRate: this.localVideoFrameRate,
          inputFrameRate: readRtcVideoStat(videoStats, ["inputFrameRate"]),
          encoderOutputFrameRate: readRtcVideoStat(videoStats, ["encoderOutputFrameRate", "encodeFrameRate"]),
          sentKBitrate: readRtcVideoStat(videoStats, ["sentKBitrate"]),
          targetKBitrate: readRtcVideoStat(videoStats, ["targetKBitrate"]),
          encodedBitrate: readRtcVideoStat(videoStats, ["encodedBitrate"]),
          videoLossRate: readRtcVideoStat(videoStats, ["videoLossRate"]),
          rtt: readRtcVideoStat(videoStats, ["rtt"]),
          statsInterval: readRtcVideoStat(videoStats, ["statsInterval"]),
          elapsedSincePublishMs: this.publishStartedAtMs > 0
            ? Number((performance.now() - this.publishStartedAtMs).toFixed(1))
            : 0,
        });

        const res = `${width}x${height}`;
        if (width > 0 && height > 0 && res !== this.lastSentResolution) {
          this.lastSentResolution = res;
        }
      }
    });

    engine.on(VERTC.events.onRemoteStreamStats, (stats: any) => {
      if (this.qualityHadFrame && stats && !stats.isScreen && stats.userId === this.qualityUserId && stats.videoStats) {
        this.remoteQuality.sample(stats.userId, stats.videoStats, performance.now());
      }
      if (stats && !stats.isScreen && typeof stats.userId === 'string' && this.isRemoteSeiUser(stats.userId)) this.recordDiagnosticStats('down', stats.videoStats, stats.audioStats);
      if (stats?.videoStats) {
        const videoStats = stats.videoStats as RtcVideoDiagnosticStats;
        const w = readRtcVideoStat(videoStats, ["width", "receivedResolutionWidth", "decodedFrameWidth"]);
        const h = readRtcVideoStat(videoStats, ["height", "receivedResolutionHeight", "decodedFrameHeight"]);
        const receivedFrameRate = readRtcVideoStat(videoStats, ["receivedFrameRate", "decoderOutputFrameRate", "renderFrameRate"]);
        const renderedFrameRate = readRtcVideoStat(videoStats, ["rendererOutputFrameRate", "renderFrameRate"]);

        this.logVideoFrameDiagnostics("receive", w, h, receivedFrameRate, stats?.userId);

        // Some mobile browser builds can omit the dedicated first-frame event. The
        // vendor's positive renderer FPS is an equivalent non-DOM confirmation.
        if (
          !stats.isScreen
          && typeof stats.userId === "string"
          && this.isRemoteSeiUser(stats.userId)
          && renderedFrameRate > 0
        ) {
          this.emitRemoteVideoFirstFrame({ userId: stats.userId, width: w, height: h }, 'render_stats');
        }

        const res = `${w}x${h}`;
        if (w > 0 && h > 0 && res !== this.lastReceivedResolution) {
          this.lastReceivedResolution = res;
        }
      }
    });

    engine.on(VERTC.events.onLocalStatsException, (exception) => {
      this.debugLog("onLocalStatsException", {
        exception,
        elapsedSincePublishMs: this.publishStartedAtMs > 0
          ? Number((performance.now() - this.publishStartedAtMs).toFixed(1))
          : null,
      });
    });

    engine.on(VERTC.events.onTrackMute, (event) => {
      this.debugLog("onTrackMute", event);
    });

    engine.on(VERTC.events.onTrackUnmute, (event) => {
      this.debugLog("onTrackUnmute", event);
    });
  }

  private emitState(): void {
    this.onStateChange?.(this.snapshot);
  }

  private ensureReadyForMessaging(): void {
    if (!this.engine || !this.joined) {
      throw new XmaxSdkError(
        "SESSION_ERROR",
        this.i18n.t("errors.rtcNotJoinedForMessaging"),
      );
    }
  }

  private bindLocalVideoPlayer(force = false): void {
    if (!this.localVideoContainer) {
      return;
    }
    const container = this.getRequiredContainer(this.localVideoContainer, "local");
    if (!force && container.childElementCount > 0) {
      this.bindUploadPreviewLayout(container);
      return;
    }
    container.replaceChildren();
    // Mirroring is applied once by RealtimeRenderSetting; keep the RTC renderer unmirrored.
    this.engine?.setLocalVideoMirrorType(MirrorType.MIRROR_TYPE_NONE);
    this.engine?.setLocalVideoPlayer(StreamIndex.STREAM_INDEX_MAIN, {
      renderDom: container,
      renderMode: resolveVideoRenderMode(container, this.renderFit),
    });
    this.bindUploadPreviewLayout(container);
  }

  private bindUploadPreviewLayout(container: HTMLElement): void {
    this.uploadPreviewLayoutObserverStop?.();
    this.uploadPreviewLayoutObserverStop = null;
    applyUploadPreviewMediaLayout(container);
    this.uploadPreviewLayoutObserverStop = observeUploadPreviewMediaLayout(container);
  }

  private clearUploadPreviewLayoutObserver(): void {
    this.uploadPreviewLayoutObserverStop?.();
    this.uploadPreviewLayoutObserverStop = null;
  }

  /** Re-bind local preview after the container becomes visible or is resized. */
  refreshLocalVideoPreview(options?: { force?: boolean }): void {
    if (!this.engine || !this.localVideoPublished || !this.localVideoContainer) {
      return;
    }
    this.bindLocalVideoPlayer(options?.force);
  }

  getLastObservedSentFrameRate(): number {
    return this.lastObservedSentFrameRate;
  }

  waitForLocalOutboundVideoReady(options?: {
    timeoutMs?: number;
    minPositiveSamples?: number;
    timeoutMessage?: string;
  }): Promise<void> {
    const timeoutMs = options?.timeoutMs ?? 10000;
    const minPositiveSamples = Math.max(1, options?.minPositiveSamples ?? 2);
    if (this.lastObservedSentFrameRate > 0 && minPositiveSamples === 1) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const waiter: LocalOutboundReadyWaiter = {
        positiveSamples: 0,
        minPositiveSamples,
        resolve: () => {
          clearTimeout(waiter.timeoutId);
          this.localOutboundReadyWaiters.delete(waiter);
          resolve();
        },
        reject: (error) => {
          clearTimeout(waiter.timeoutId);
          this.localOutboundReadyWaiters.delete(waiter);
          reject(error);
        },
        timeoutId: setTimeout(() => {
          waiter.reject(new XmaxSdkError(
            "WEB_RTC_ERROR",
            options?.timeoutMessage ?? "Local video publish produced no outbound frames",
          ));
        }, timeoutMs),
      };
      this.localOutboundReadyWaiters.add(waiter);
    });
  }

  private rejectLocalOutboundReadyWaiters(message: string): void {
    if (this.localOutboundReadyWaiters.size === 0) {
      return;
    }
    const error = new XmaxSdkError('WEB_RTC_ERROR', message);
    Array.from(this.localOutboundReadyWaiters).forEach((waiter) => waiter.reject(error));
  }

  /** Immediately abandon a wedged engine without awaiting unpublish/leave promises. */
  forceDestroy(options?: { preserveExternalTrack?: boolean }): void {
    this.tearingDown = true;
    clearTimeout(this.reconnectTimer);
    const engine = this.engine;
    this.engine = null;
    try {
      engine?.destroy();
      this.diagnostics?.record('sdk_rtc_teardown', { operation: 'destroy_engine', outcome: 'success', forced: true });
    } catch {
      this.diagnostics?.record('sdk_rtc_teardown', { operation: 'destroy_engine', outcome: 'error', forced: true });
    }

    if (!options?.preserveExternalTrack) {
      try {
        this.externalVideoTrack?.stop();
      } catch {
      }
      try {
        this.externalAudioTrack?.stop();
      } catch {
      }
    }

    this.rejectLocalOutboundReadyWaiters("RTC engine was destroyed");
    this.nativeSampler?.stop();
    this.nativeSampler = undefined;
    this.stopRemoteQualityWatch();
    this.clearRemoteFirstOutputWatch();
    this.clearLocalOutboundWatch();
    this.stopRemoteFrameWatch();
    this.seiSender.stop();
    this.clearSeiGateFallbackTimer();
    this.clearSeiMatchedDelayTimer();
    this.clearUploadPreviewLayoutObserver();
    this.users.clear();
    this.currentJoinInfo = null;
    this.joined = false;
    this.localVideoPublished = false;
    this.localAudioPublished = false;
    this.internalVideoCaptureStarted = false;
    this.internalAudioCaptureStarted = false;
    this.localVideoDegradationPreference = null;
    this.localVideoSourceType = VideoSourceType.VIDEO_SOURCE_TYPE_INTERNAL;
    this.externalVideoTrack = null;
    this.externalAudioTrack = null;
    this.remoteVideoUserId = null;
    this.pendingRemoteVideoUserId = null;
    this.pendingRemoteVideoMediaType = MediaType.VIDEO;
    this.subscribedRemoteUserId = null;
    this.subscribedRemoteMediaType = MediaType.VIDEO;
    this.joinedAtMs = 0;
    this.publishStartedAtMs = 0;
    this.lastRoomStartEventAtMs = 0;
    this.localStreamStatsCount = 0;
    this.firstNonZeroSentFrameRateAtMs = 0;
    this.lastObservedSentFrameRate = 0;
    this.lastSentResolution = "";
    this.lastReceivedResolution = "";
    this.clearVideoContainers();
    this.emitState();
  }

  private async switchToExternalVideoSource(track: MediaStreamTrack): Promise<void> {
    if (!this.engine) {
      return;
    }

    this.externalVideoTrack = track;
    await this.engine.setVideoSourceType(StreamIndex.STREAM_INDEX_MAIN, VideoSourceType.VIDEO_SOURCE_TYPE_EXTERNAL);
    await this.engine.setExternalVideoTrack(StreamIndex.STREAM_INDEX_MAIN, track);
    this.localVideoSourceType = VideoSourceType.VIDEO_SOURCE_TYPE_EXTERNAL;
  }

  private async switchToExternalAudioSource(track: MediaStreamTrack): Promise<void> {
    if (!this.engine) {
      return;
    }
    const engine = this.engine as unknown as {
      setAudioSourceType?: (streamIndex: StreamIndex, sourceType: AudioSourceType) => Promise<void> | void;
      setExternalAudioTrack?: (streamIndex: StreamIndex, track: MediaStreamTrack) => Promise<void> | void;
    };
    if (typeof engine.setAudioSourceType !== "function") {
      this.log("warn", "[Xmax][RTC] setAudioSourceType API is unavailable");
      return;
    }
    if (typeof engine.setExternalAudioTrack !== "function") {
      this.log("warn", "[Xmax][RTC] setExternalAudioTrack API is unavailable");
      return;
    }
    await engine.setAudioSourceType(
      StreamIndex.STREAM_INDEX_MAIN,
      AudioSourceType.AUDIO_SOURCE_TYPE_EXTERNAL,
    );
    this.externalAudioTrack = track;
    await engine.setExternalAudioTrack(StreamIndex.STREAM_INDEX_MAIN, track);
    this.log("info", "[Xmax][RTC] external audio track attached", {
      readyState: track.readyState,
      enabled: track.enabled,
      muted: track.muted,
    });
  }

  private async switchToInternalAudioSource(): Promise<void> {
    if (!this.engine) {
      return;
    }
    const engine = this.engine as unknown as {
      setAudioSourceType?: (streamIndex: StreamIndex, sourceType: AudioSourceType) => Promise<void> | void;
    };
    if (typeof engine.setAudioSourceType !== "function") {
      return;
    }
    await engine.setAudioSourceType(
      StreamIndex.STREAM_INDEX_MAIN,
      AudioSourceType.AUDIO_SOURCE_TYPE_INTERNAL,
    );
  }

  private async switchToInternalVideoSource(shouldSetConfig = true): Promise<void> {
    const engine = this.engine;
    if (!engine) {
      return;
    }

    if (this.localVideoSourceType === VideoSourceType.VIDEO_SOURCE_TYPE_INTERNAL) {
      return;
    }

    await engine.setVideoSourceType(StreamIndex.STREAM_INDEX_MAIN, VideoSourceType.VIDEO_SOURCE_TYPE_INTERNAL);
    if (this.engine !== engine) return;
    this.localVideoSourceType = VideoSourceType.VIDEO_SOURCE_TYPE_INTERNAL;
    if (shouldSetConfig && this.externalVideoTrack) {
      this.externalVideoTrack = null;
    }
  }

  private async applyVideoEncoderPreference(
    size: VideoSize,
    publishOptions: RtcVideoEncoderPreferenceOptions,
    options?: { throwOnFailure?: boolean },
  ): Promise<boolean> {
    if (!this.engine) {
      return false;
    }

    const width = size[0];
    const height = size[1];
    const frameRate = publishOptions.fps ?? 24;
    const maxKbps = publishOptions.maxKbps ?? DEFAULT_REALTIME_STREAM_MAX_KBPS;
    const contentHint = publishOptions.contentHint ?? DEFAULT_REALTIME_STREAM_CONTENT_HINT;
    const diagnostics = {
      width,
      height,
      frameRate,
      maxKbps,
      contentHint,
      preferCodecName: VideoCodecType.H264,
    };

    try {
      await this.engine.setVideoEncoderConfig({
        width,
        height,
        frameRate,
        maxKbps,
        contentHint,
        preferCodecName: VideoCodecType.H264,
      });
      this.localVideoDegradationPreference = contentHint === "motion"
        ? "maintain-framerate"
        : "maintain-resolution";

      this.log("info", this.i18n.t("rtc.encoderPreferenceSet"), size);
      return true;
    } catch (error) {
      this.log("warn", this.i18n.t("rtc.encoderPreferenceFailed"), error);
      this.log("error", "[Xmax][RTC] setVideoEncoderConfig failed", {
        ...diagnostics,
        error,
      });
      if (options?.throwOnFailure) {
        throw normalizeRtcError(
          error,
          "WEB_RTC_ERROR",
          "Failed to configure RTC video encoder",
        );
      }
      return false;
    }
  }

  /** Explicitly apply the content-hint degradation preference to the active WebRTC sender. */
  private async applyLocalVideoDegradationPreference(track: MediaStreamTrack): Promise<void> {
    const preference = this.localVideoDegradationPreference;
    const peerConnection = this.engine?.peerConnection;
    if (!preference || !peerConnection) {
      this.debugLog("local video degradation preference unavailable", {
        preference,
        hasPeerConnection: Boolean(peerConnection),
      });
      return;
    }

    try {
      const senders = peerConnection.getSenders();
      const sender = senders.find((candidate) => candidate.track === track)
        ?? senders.find((candidate) => candidate.track?.kind === "video");
      if (!sender) {
        this.debugLog("local video sender unavailable", { preference });
        return;
      }

      const parameters = sender.getParameters();
      if (parameters.degradationPreference === preference) {
        return;
      }
      parameters.degradationPreference = preference;
      await sender.setParameters(parameters);
      this.log("info", "[Xmax][RTC] Local video degradation preference set", {
        preference,
      });
    } catch (error) {
      // Browser support differs; contentHint remains the standards-based fallback.
      this.log("warn", "[Xmax][RTC] Failed to set local video degradation preference", {
        preference,
        error,
      });
    }
  }

  private async attachRemoteVideo(userId: string, mediaType: MediaType): Promise<void> {
    if (!this.engine || !this.currentJoinInfo) {
      return;
    }

    const preferredBotUserId = this.currentJoinInfo.botName?.trim() || null;
    const shouldReplaceCurrent =
      this.remoteVideoUserId === null ||
      userId === preferredBotUserId ||
      this.remoteVideoUserId !== preferredBotUserId;

    if (!shouldReplaceCurrent) {
      return;
    }

    const subscriptionMediaType = this.resolveRemoteSubscriptionMediaType(mediaType);
    this.pendingRemoteVideoUserId = userId;
    this.pendingRemoteVideoMediaType = subscriptionMediaType;

    // Subscribe immediately so frames are not dropped while SEI gate delays render.
    await this.ensureRemoteStreamSubscribed(userId, subscriptionMediaType);

    if (!this.canRenderRemoteVideo(userId)) {
      this.log("info", this.i18n.t("rtc.remoteWaitSei", { userId }), {
        expectedRemoteSei: this.expectedRemoteSei,
        lastRemoteSei: this.lastRemoteSei,
        lastRemoteSeiUserId: this.lastRemoteSeiUserId,
      });
      return;
    }

    const container = this.remoteVideoContainer;
    if (!container) {
      this.log("warn", this.i18n.t("rtc.remoteContainerNotReady"), { userId, mediaType });
      return;
    }
    await this.mountRemoteVideoPlayer(userId, container, subscriptionMediaType);
    this.remoteVideoUserId = userId;
    this.pendingRemoteVideoUserId = null;
    this.emitState();
    this.startRemoteFrameWatch(userId);
    this.emitRemotePlayerMounted();
    this.log("success", this.i18n.t("rtc.remotePlayerBound", { userId }));
  }

  private async ensureRemoteStreamSubscribed(userId: string, mediaType: MediaType): Promise<void> {
    if (!this.engine) {
      return;
    }
    const sameUserSubscribed = this.subscribedRemoteUserId === userId;
    if (sameUserSubscribed && this.isSubscriptionCovered(this.subscribedRemoteMediaType, mediaType)) {
      return;
    }

    const subscribeStartedAt = performance.now();
    try {
      await this.engine.subscribeStream(userId, mediaType);
    } catch (error) {
      throw normalizeRtcError(error, "WEB_RTC_ERROR", "Failed to subscribe to RTC stream");
    }
    this.subscribedRemoteUserId = userId;
    this.subscribedRemoteMediaType = mediaType;
    this.diagnostics?.record('sdk_rtc_subscribe_remote_track', {
      track_kind: this.supportsVideo(mediaType) ? 'video' : 'audio',
      subscribe_duration_ms: Math.round(performance.now() - subscribeStartedAt),
    });
    this.debugLog("ensureRemoteStreamSubscribed", { userId, mediaType });
  }

  private bindRemoteVideoPlayerToContainer(userId: string, container: HTMLElement): void {
    if (!this.engine) {
      return;
    }

    try {
      this.engine.setRemoteVideoPlayer(StreamIndex.STREAM_INDEX_MAIN, {
        userId,
        renderDom: container,
        renderMode: resolveVideoRenderMode(container, this.renderFit),
      });
    } catch (error) {
      throw normalizeRtcError(error, "WEB_RTC_ERROR", "Failed to bind RTC remote video player");
    }
    this.bindUploadPreviewLayout(container);
  }

  private async mountRemoteVideoPlayer(
    userId: string,
    container: HTMLElement,
    mediaType: MediaType,
    force = false,
  ): Promise<void> {
    if (!this.engine) {
      return;
    }
    if (
      !force &&
      this.remoteVideoUserId === userId &&
      container === this.remoteVideoContainer &&
      container.childElementCount > 0
    ) {
      this.debugLog("mountRemoteVideoPlayer skipped (already mounted)", { userId });
      return;
    }
    this.debugLog(force ? "mountRemoteVideoPlayer (force)" : "mountRemoteVideoPlayer", {
      userId,
      mediaType,
      childCountBefore: container.childElementCount,
    });
    this.stopRemoteFrameWatch();
    container.replaceChildren();
    await this.ensureRemoteStreamSubscribed(userId, mediaType);
    this.bindRemoteVideoPlayerToContainer(userId, container);
  }

  private startRemoteFrameWatch(userId: string): void {
    this.stopRemoteFrameWatch();

    let lastFrameAt = 0;
    let hadFirstFrame = false;
    let rvfcHandle: number | null = null;
    let watchedVideo: HTMLVideoElement | null = null;
    const onVisibility = () => { if (hadFirstFrame) lastFrameAt = performance.now() };
    if (typeof document !== 'undefined') document.addEventListener?.('visibilitychange', onVisibility);

    const onFrame = () => {
      lastFrameAt = performance.now();
      if (watchedVideo && 'requestVideoFrameCallback' in watchedVideo) this.remoteQuality.presented(lastFrameAt);
      if (!hadFirstFrame) {
        this.emitRemoteVideoFirstFrame({
          userId,
          width: watchedVideo?.videoWidth ?? 0,
          height: watchedVideo?.videoHeight ?? 0,
        }, watchedVideo && 'requestVideoFrameCallback' in watchedVideo ? 'video_frame_callback' : 'timeupdate');
      }
      hadFirstFrame = true;
    };

    const bindRvfc = (video: HTMLVideoElement) => {
      if (watchedVideo === video && rvfcHandle !== null) {
        return;
      }
      if (watchedVideo && watchedVideo !== video) {
        watchedVideo.removeEventListener("timeupdate", onFrame);
        if (rvfcHandle !== null) (watchedVideo as VideoWithFrameCallback).cancelVideoFrameCallback?.(rvfcHandle);
        rvfcHandle = null;
      }
      watchedVideo = video;
      const rvfcVideo = video as VideoWithFrameCallback;
      if (rvfcVideo.requestVideoFrameCallback) {
        const schedule = () => {
          if (this.remoteVideoUserId !== userId || watchedVideo !== video) {
            return;
          }
          rvfcHandle = rvfcVideo.requestVideoFrameCallback!(() => {
            if (watchedVideo !== video) return;
            onFrame();
            schedule();
          });
        };
        schedule();
        return;
      }
      video.addEventListener("timeupdate", onFrame);
    };

    const bindCurrentVideo = () => {
      if (!this.remoteVideoContainer) {
        return;
      }
      const video = this.remoteVideoContainer instanceof HTMLVideoElement
        ? this.remoteVideoContainer
        : this.remoteVideoContainer.querySelector("video");
      if (video) {
        bindRvfc(video);
      }
    };

    bindCurrentVideo();

    const pollTimer = setInterval(() => {
      if (this.reconnectStartedAt !== undefined) {
        lastFrameAt = performance.now();
        return;
      }
      if (hadFirstFrame) lastFrameAt = Math.max(lastFrameAt, this.connectionRecoveredAt);
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        lastFrameAt = performance.now();
        return;
      }
      if (this.remoteVideoUserId !== userId || !this.remoteVideoContainer) {
        return;
      }

      bindCurrentVideo();

      if (!hadFirstFrame || lastFrameAt === 0) {
        return;
      }

      const now = performance.now();
      const stallMs = now - lastFrameAt;
      if (stallMs < REMOTE_OUTPUT_STALL_MS) {
        return;
      }

      if (now < this.remoteOutputRecoveryCooldownUntil) {
        return;
      }

      this.remoteOutputRecoveryCooldownUntil = now + REMOTE_OUTPUT_RECOVERY_COOLDOWN_MS;
      this.debugLog("remote output stalled", { userId, stallMs });
      this.log("warn", this.i18n.t("rtc.remoteOutputStalled"), { userId, stallMs });
      this.onRemoteOutputStalled?.({ userId, stallMs });

      hadFirstFrame = false;
      lastFrameAt = 0;
    }, 3000);

    this.remoteFrameWatchStop = () => {
      if (typeof document !== 'undefined') document.removeEventListener?.('visibilitychange', onVisibility);
      clearInterval(pollTimer);
      if (watchedVideo) {
        watchedVideo.removeEventListener("timeupdate", onFrame);
      }
      if (rvfcHandle !== null && watchedVideo) {
        const rvfcVideo = watchedVideo as VideoWithFrameCallback;
        rvfcVideo.cancelVideoFrameCallback?.(rvfcHandle);
      }
      rvfcHandle = null;
      watchedVideo = null;
    };
  }

  private stopRemoteFrameWatch(): void {
    this.remoteFrameWatchStop?.();
    this.remoteFrameWatchStop = null;
  }

  private isRemoteSeiUser(userId: string): boolean {
    const localUserId = this.currentJoinInfo?.userId?.trim();
    if (localUserId && userId === localUserId) {
      return false;
    }

    const botUserId = this.currentJoinInfo?.botName?.trim();
    if (botUserId) {
      return userId === botUserId;
    }

    return true;
  }
  
  /** Always mount remote player — SEI gate only affects display matching, not subscription. */
  private canRenderRemoteVideo(_userId: string): boolean {
    return true;
  }

  /** Whether remote SEI matches the active task (display gate). */
  private isSeiMatched(): boolean {
    if (this.seiSupport === "unsupported") {
      return true;
    }
    if (!this.expectedRemoteSei) {
      return true;
    }
    return this.lastRemoteSei === this.expectedRemoteSei;
  }

  /** Detect whether the browser can send and receive H.264 SEI messages. */
  private ensureSeiSupport(): void {
    if (this.seiSupport !== "unknown") {
      return;
    }

    void VERTC.getSupportedCodecs()
      .then((codecs) => {
        const supported = codecs.some((c) => c.toLowerCase() === "h264" || c.toLowerCase() === "h.264");
        this.seiSupport = supported ? "supported" : "unsupported";

        if (this.seiSupport === "unsupported" && !this.hasLoggedSeiUnsupported) {
          this.hasLoggedSeiUnsupported = true;
          this.log("warn", this.i18n.t("rtc.seiUnsupported"), codecs);
        }

        if (this.seiSupport === "unsupported") {
          this.expectedRemoteSei = null;
          this.emitSeiGateState();
          this.refreshRemoteVideoBindingBestEffort();
        } else if (this.seiSender.configured) {
          this.seiSender.start();
          this.emitSeiGateState();
          this.refreshRemoteVideoBindingBestEffort();
        }
      })
      .catch((error) => {
        this.seiSupport = "unsupported";
        if (!this.hasLoggedSeiUnsupported) {
          this.hasLoggedSeiUnsupported = true;
          this.log("warn", this.i18n.t("rtc.getCodecsFailed"), error);
        }
        this.expectedRemoteSei = null;
        this.emitSeiGateState();
        this.refreshRemoteVideoBindingBestEffort();
      });
  }

  private supportsVideo(mediaType: MediaType): boolean {
    return mediaType === MediaType.VIDEO || mediaType === MediaType.AUDIO_AND_VIDEO;
  }
  private supportsAudio(mediaType: MediaType): boolean {
    return mediaType === MediaType.AUDIO || mediaType === MediaType.AUDIO_AND_VIDEO;
  }
  private resolveRemoteSubscriptionMediaType(publishedMediaType: MediaType): MediaType {
    if (this.playRemoteAudio || !this.supportsAudio(publishedMediaType)) {
      return publishedMediaType;
    }
    return MediaType.VIDEO;
  }
  private isSubscriptionCovered(current: MediaType, requested: MediaType): boolean {
    const currentHasVideo = this.supportsVideo(current);
    const currentHasAudio = this.supportsAudio(current);
    const requestedHasVideo = this.supportsVideo(requested);
    const requestedHasAudio = this.supportsAudio(requested);
    return (!requestedHasVideo || currentHasVideo) && (!requestedHasAudio || currentHasAudio);
  }
  private clearVideoContainers(): void {
    this.stopRemoteFrameWatch();
    this.clearContainer(this.localVideoContainer);
    this.clearContainer(this.remoteVideoContainer);
  }

  private clearContainer(container: HTMLElement | null): void {
    container?.replaceChildren();
  }

  private getRequiredContainer(
    container: HTMLElement | null,
    containerType: "local" | "remote",
  ): HTMLElement {
    if (!container) {
      throw new XmaxSdkError(
        "INVALID_INPUT",
        `missing ${containerType} video container`,
      );
    }
    return container;
  }

  private emitRemotePlayerMounted(): void {
    this.onRemotePlayerMounted?.();
  }

  private parseRoomEventPayload(payload: unknown): Record<string, unknown> | null {
    const getMessageText = (input: unknown): string | null => {
      if (typeof input === "string") {
        return input.trim() || null;
      }
      if (!input || typeof input !== "object") {
        return null;
      }
      const obj = input as Record<string, unknown>;
      const direct = obj.message ?? obj.msg ?? obj.data ?? obj.payload;
      if (typeof direct === "string") {
        return direct.trim() || null;
      }
      return null;
    };

    const text = getMessageText(payload);
    if (!text) {
      return null;
    }

    try {
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== "object") {
        return null;
      }
      return parsed as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private recordGenerationAckFromRoomEvent(event: Record<string, unknown>): void {
    if (event.event !== "video_started") {
      return;
    }
    const generationId = typeof event.uid === "string"
      ? event.uid.trim()
      : typeof event.session_uid === "string"
        ? event.session_uid.trim()
        : "";
    if (!generationId || generationId !== this.activeGenerationId) {
      return;
    }
    this.diagnostics?.generationAckReceived(generationId, {
      ok: true,
      source_event: "video_started",
    });
  }

  private log(level: RtcLogEntry["level"], message: string, data?: unknown): void {
    this.onLog?.({ level, message, data });
  }

  private logTiming(
    stage: string,
    startedAt: number,
    data?: Record<string, unknown>,
  ): void {
    if (!ENABLE_RTC_TIMING_LOGS) {
      return;
    }
    this.log("info", `[RTC Timing] ${stage}`, {
      elapsedMs: Number((performance.now() - startedAt).toFixed(1)),
      ...data,
    });
  }

  /** RTC stream stats are emitted about every two seconds, so this does not log per frame. */
  private logVideoFrameDiagnostics(
    direction: "send" | "receive",
    width: number,
    height: number,
    frameRate: number,
    userId?: string,
    details?: Record<string, number>,
  ): void {
    const label = direction === "send" ? "Send" : "Receive";
    const resolution = width > 0 && height > 0 ? `${width}x${height}` : "unknown";
    const fps = Number(frameRate.toFixed(2));
    this.log("info", `[Xmax][RTC] ${label} video: ${resolution} @ ${fps} fps`, {
      direction,
      width,
      height,
      frameRate: fps,
      ...(userId ? { userId } : {}),
      ...details,
    });
  }

  private logLocalStreamStats(stats: LocalStreamStats): void {
    if (!this.localVideoPublished) {
      return;
    }

    this.localStreamStatsCount += 1;
    const nowMs = performance.now();
    const sentFrameRate = stats.videoStats?.sentFrameRate ?? 0;

    if (sentFrameRate > 0 && this.firstNonZeroSentFrameRateAtMs === 0) {
      this.firstNonZeroSentFrameRateAtMs = nowMs;
    }

    this.lastObservedSentFrameRate = sentFrameRate;
    this.localOutboundReadyWaiters.forEach((waiter) => {
      if (sentFrameRate > 0) {
        waiter.positiveSamples += 1;
        if (waiter.positiveSamples >= waiter.minPositiveSamples) {
          waiter.resolve();
        }
        return;
      }
      waiter.positiveSamples = 0;
    });
    this.evaluateLocalOutboundWatch(sentFrameRate, nowMs);
  }

  private evaluateLocalOutboundWatch(sentFrameRate: number, nowMs: number): void {
    const state = this.localOutboundWatch;
    if (!state || state.triggered) {
      return;
    }

    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      state.consecutiveZeroStats = 0;
      return;
    }

    if (sentFrameRate > 0) {
      state.consecutiveZeroStats = 0;
      state.lastPositiveAtMs = nowMs;
      return;
    }

    if (nowMs < state.graceUntilMs) {
      state.consecutiveZeroStats = 0;
      return;
    }

    state.consecutiveZeroStats += 1;
    const stallMs = nowMs - state.lastPositiveAtMs;
    if (
      state.consecutiveZeroStats >= state.minZeroSamples &&
      stallMs >= state.minStallMs
    ) {
      this.triggerLocalOutboundStall("zero-sent-frame-rate");
    }
  }

  private triggerLocalOutboundStall(
    reason: LocalOutboundStallInfo["reason"],
  ): void {
    const state = this.localOutboundWatch;
    if (!state || state.triggered || this.reconnectStartedAt !== undefined) {
      return;
    }

    state.triggered = true;
    const nowMs = performance.now();
    const info: LocalOutboundStallInfo = {
      reason,
      consecutiveZeroStats: state.consecutiveZeroStats,
      stallMs: Math.max(0, Math.round(nowMs - state.lastPositiveAtMs)),
      lastSentFrameRate: this.lastObservedSentFrameRate,
    };
    this.debugLog("local outbound stalled", info);
    state.onStalled(info);
  }

  private debugLog(message: string, data?: unknown): void {
    if (!this.debug) {
      return;
    }
    this.log("info", `[debug] ${message}`, data);
  }
}
